import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useFeatureUsage, PRODUCT_AREAS } from '../../hooks/useFeatureUsage';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Autocomplete,
  TextField,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Tooltip,
  Switch,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Collapse,
  Tabs,
  Tab,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import {
  CloudUpload as CloudUploadIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  HourglassEmpty as HourglassEmptyIcon,
  LocationOn as LocationOnIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  Bolt as BoltIcon,
  DeleteForever as DeleteForeverIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  DirectionsCar as DirectionsCarIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  TipsAndUpdates as TipsAndUpdatesIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Tune as TuneIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Marker, Popup, useMap, Polyline } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point as turfPoint, polygon as turfPolygon } from '@turf/helpers';
import Papa from 'papaparse';
import { useSnackbar } from 'notistack';
import { useInsight } from '../../contexts/InsightContext';
import apiClient from '../../utils/apiClient';
import { schoolsApi } from '../../utils/apiService';
import { formatSchoolName } from '../../utils/nameFormatter';
import { driveTimeColors } from '../../utils/unifiedMapUtils';
import { filterSchools, hasGradeOverlap } from '../../utils/schoolUtils';
import { getAcademicColorStyle } from '../../utils/academicUtils';
import {
  getInnermostDriveTime,
  driveTimeLabelForPoint,
  hasStoredPreciseDriveTimes,
  focusDriveTimeFromMedian,
  focusAdviceCopy,
  parseNewToSchool,
  neighborhoodDisplayLabel,
  nearbySchoolMetrics,
  parseOverpassAmenities,
  summarizeNeighborhoods,
  summarizeDistricts,
  districtLabelForPoint,
  OUTSIDE_DISTRICT_LABEL,
  studentPinColor,
  geojsonBounds,
  bboxKey,
  parsePlaceType,
  placeTypeColor,
  placeTypeLetter,
  PORTFOLIO_SCOPES,
  normalizeCounty,
  haversineMiles,
  portfolioSchoolsOutsideDriveTimes,
} from '../../utils/catchmentMapUtils';
import {
  EDC_LOGO_PATH,
  fetchImageAsDataUrl,
  captureLeafletMapImage,
  buildCatchmentOnePagerHtml,
  catchmentOnePagerFilename,
  downloadCatchmentOnePagerHtml,
} from '../../utils/catchmentOnePagerExport';
import StudentCatchmentTour from './StudentCatchmentTour';
import {
  CATCHMENT_TOUR_STEPS,
  readCatchmentTourDismissed,
  shouldShowCatchmentTour,
  writeCatchmentTourDismissed,
} from './catchmentTour';
import CatchmentSchoolPicker from './CatchmentSchoolPicker';
import { useTeamSchoolCache } from '../../hooks/teamCacheService';
import useSchoolStore from '../../stores/schoolStore';
import { findAssignedSchool } from './assignedSchool';

// Fix Leaflet default icon URLs broken by webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_STUDENT_ROWS = 1500;
const MAX_PLACE_ROWS = 200;
const REQUIRED_COLUMNS = ['street', 'city', 'state', 'zip_code'];
const ALLOWED_COLUMNS = ['street', 'city', 'state', 'zip_code', 'new_to_school'];
const PLACE_REQUIRED_COLUMNS = ['name', 'type', 'street', 'city', 'state', 'zip_code'];
const PLACE_KNOWN_TYPES = [
  { kind: 'bus_stop', slug: 'bus_stop', examples: ['bus_stop', 'bus stop', 'bus-stop'] },
  { kind: 'health_center', slug: 'health_center', examples: ['health_center', 'health center', 'health-center'] },
  { kind: 'recreation_center', slug: 'recreation_center', examples: ['recreation_center', 'recreation center', 'recreation-center'] },
];
const DRIVE_TIMES = [5, 10, 15];
const STEPS = ['Select School', 'Upload Student Addresses', 'Results'];

const BASEMAPS = {
  streets: {
    url: 'https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>',
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri',
  },
  detailed: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  },
};

const NEW_STUDENT_COLOR = '#0ea5e9';
const EXISTING_STUDENT_COLOR = '#16a34a';
const UNKNOWN_STUDENT_COLOR = '#64748b';
const LIBRARY_COLOR = '#7c3aed';
const COMMUNITY_CENTER_COLOR = '#0d9488';

const DRIVE_TIME_LABELS = {
  5: '5-Minute Drive',
  10: '10-Minute Drive',
  15: '15-Minute Drive',
};

const RING_LABELS = {
  5: 'Within 5 min',
  10: '5 – 10 min',
  15: '10 – 15 min',
};

const ZONE_RANK = { 5: 0, 10: 1, 15: 2, outside: 3 };
const RANK_TO_ZONE = [5, 10, 15, 'outside'];
const NEARBY_CHARTER_COLOR = '#c026d3';
const NEARBY_DISTRICT_COLOR = '#2563eb';
const NEARBY_GOVERNANCE_OPTIONS = ['Charter', 'District'];
const TEAM_DRIVE_TIME_COLOR = '#ea580c';

const mapControlsReveal = keyframes`
  0% {
    opacity: 0;
    transform: translateY(8px);
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.34), 0 8px 22px rgba(15, 23, 42, 0.06);
  }
  45% {
    opacity: 1;
    transform: translateY(0);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.22), 0 10px 26px rgba(15, 23, 42, 0.10);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0), 0 6px 18px rgba(15, 23, 42, 0.08);
  }
`;

const CATCHMENT_TABLE_SX = {
  '& .MuiTableCell-root': {
    fontSize: '0.875rem',
    py: 1,
    px: 2,
  },
  '& .MuiTableCell-head': {
    backgroundColor: 'primary.main',
    color: 'white',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    borderBottom: 'none',
  },
};

const CATCHMENT_TABLE_ROW_SX = {
  '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
  '&:hover': { backgroundColor: 'action.selected' },
  transition: 'background-color 0.15s ease',
};

function medianDriveZone(zones) {
  if (!zones?.length) return null;
  const ranks = zones.map((z) => ZONE_RANK[z] ?? 3).sort((a, b) => a - b);
  const idx = ranks.length % 2 === 0 ? ranks.length / 2 : Math.floor(ranks.length / 2);
  return RANK_TO_ZONE[ranks[idx]];
}

const INITIAL_PROCESSING_STEPS = {
  polygons: 'pending',
  geocoding: 'pending',
  analysis: 'pending',
  routing: 'pending',
};

function roundCoord(value) {
  return Math.round(Number(value) * 1e5) / 1e5;
}

function coordKey(lat, lng) {
  return `${roundCoord(lat)},${roundCoord(lng)}`;
}

function medianMinutes(values) {
  const nums = values
    .filter((v) => typeof v === 'number' && Number.isFinite(v))
    .sort((a, b) => a - b);
  if (!nums.length) return null;
  const mid = Math.floor(nums.length / 2);
  return nums.length % 2 === 1 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
}

function attachDriveMinutes(markers, entries) {
  const byCoord = new Map();
  (entries || []).forEach((e) => {
    if (e.latitude == null || e.longitude == null) return;
    byCoord.set(coordKey(e.latitude, e.longitude), e.drive_time_minutes);
  });
  return markers.map((m) => {
    const key = coordKey(m.lat, m.lng);
    if (!byCoord.has(key)) return m;
    return { ...m, driveTimeMinutes: byCoord.get(key) };
  });
}

function attachNeighborhoods(markers, entries) {
  const byCoord = new Map();
  (entries || []).forEach((e) => {
    if (e.latitude == null || e.longitude == null) return;
    if (e.neighborhood) byCoord.set(coordKey(e.latitude, e.longitude), e.neighborhood);
  });
  return markers.map((m) => {
    const key = coordKey(m.lat, m.lng);
    if (!byCoord.has(key)) return m;
    return { ...m, neighborhood: byCoord.get(key) };
  });
}

function entryToMarker(e) {
  return {
    lat: e.latitude,
    lng: e.longitude,
    address: e.formatted_address || `${e.street}, ${e.city}, ${e.state} ${e.zip_code}`,
    rawAddress: `${e.street?.trim().toLowerCase()}, ${e.city?.trim().toLowerCase()}, ${e.state?.trim().toLowerCase()} ${e.zip_code?.trim()}`,
    driveTimeMinutes: e.drive_time_minutes != null ? e.drive_time_minutes : undefined,
    newToSchool: e.new_to_school === true ? true : e.new_to_school === false ? false : null,
    neighborhood: e.neighborhood || null,
    city: e.city || '',
    zipCode: e.zip_code || '',
  };
}

function poiMapIcon(kind) {
  const bg = kind === 'library' ? LIBRARY_COLOR : COMMUNITY_CENTER_COLOR;
  const letter = kind === 'library' ? 'L' : 'C';
  return L.divIcon({
    className: 'catchment-poi-icon',
    html: `<div style="width:18px;height:18px;border-radius:4px;background:${bg};color:#fff;font:700 10px/18px sans-serif;text-align:center;box-shadow:0 1px 3px rgba(0,0,0,.4)">${letter}</div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -10],
  });
}

function placeMapIcon(kind) {
  const bg = placeTypeColor(kind);
  const letter = placeTypeLetter(kind);
  return L.divIcon({
    className: 'catchment-place-icon',
    html: `<div style="width:20px;height:20px;border-radius:50%;background:${bg};color:#fff;font:700 10px/20px sans-serif;text-align:center;box-shadow:0 0 0 2px #fff,0 1px 3px rgba(0,0,0,.4)">${letter}</div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    popupAnchor: [0, -11],
  });
}

function entryToPlace(p) {
  const parsed = parsePlaceType(p.type_label || p.place_kind);
  return {
    id: p.id,
    name: p.name,
    kind: parsed.kind,
    typeLabel: parsed.label,
    lat: p.latitude,
    lng: p.longitude,
    street: p.street,
    city: p.city,
    state: p.state,
    zipCode: p.zip_code,
    formattedAddress: p.formatted_address,
  };
}

function formatRoutingError(err) {
  const raw = err?.message || 'Unknown error';
  if (/single region/i.test(raw)) {
    return 'Some addresses geocoded too far from campus to route. Local students can still get drive times — try opening this school again.';
  }
  const detail = raw.match(/'details': \['([^']+)'\]/);
  if (detail) return detail[1];
  return raw;
}

function colorForMinutes(minutes) {
  if (minutes == null || !Number.isFinite(minutes)) return '#9ca3af';
  if (minutes <= 5) return driveTimeColors[5];
  if (minutes <= 10) return driveTimeColors[10];
  if (minutes <= 15) return driveTimeColors[15];
  return '#9ca3af';
}

function CatchmentShareTable({ rows, nameHeader, selectedLabel, onRowClick }) {
  return (
    <TableContainer sx={{ maxHeight: 280 }}>
      <Table size="small" stickyHeader sx={CATCHMENT_TABLE_SX}>
        <TableHead>
          <TableRow>
            <TableCell>{nameHeader}</TableCell>
            <TableCell align="right">Students</TableCell>
            <TableCell align="right">Share</TableCell>
            <TableCell align="right">Median drive</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const driveColor = row.medianMinutes != null
              ? colorForMinutes(row.medianMinutes)
              : null;
            const isSelected = selectedLabel === row.label;
            return (
              <TableRow
                key={row.label}
                hover
                selected={isSelected}
                onClick={() => onRowClick?.(row)}
                sx={{
                  ...CATCHMENT_TABLE_ROW_SX,
                  cursor: 'pointer',
                  '&.Mui-selected': {
                    bgcolor: alpha('#1976d2', 0.12),
                  },
                }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  sx={{ fontWeight: i === 0 ? 600 : 400 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {row.label}
                    {i === 0 && (
                      <Chip
                        label="Largest"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          bgcolor: alpha('#1976d2', 0.12),
                          color: 'primary.main',
                        }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {row.count.toLocaleString()}
                </TableCell>
                <TableCell align="right">
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 1,
                      minWidth: 108,
                    }}
                  >
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, row.share)}
                      sx={{
                        width: 56,
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' },
                      }}
                    />
                    <Box sx={{ fontWeight: 600, minWidth: 36, textAlign: 'right' }}>
                      {row.share}%
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {row.medianMinutes != null ? (
                    <Chip
                      label={`${Math.round(row.medianMinutes)} min`}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        bgcolor: alpha(driveColor, 0.16),
                        color: driveColor,
                      }}
                    />
                  ) : (
                    <Box sx={{ color: 'text.secondary' }}>—</Box>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function toExplorerGrades(grades) {
  return grades.map((g) => {
    if (g === 'K') return 'Kindergarten';
    if (g === 'PK') return 'Pre-Kindergarten';
    return g;
  });
}

const GRADE_ORDER = [
  'PK', 'K', '1', '2', '3', '4', '5',
  '6', '7', '8', '9', '10', '11', '12',
];

// Normalize common grade label variants to the canonical form used in GRADE_ORDER.
const GRADE_ALIASES = {
  KG: 'K', KINDER: 'K', KINDERGARTEN: 'K', '0': 'K',
  PRE: 'PK', 'PRE-K': 'PK', PREK: 'PK', PREKINDERGARTEN: 'PK', PRESCHOOL: 'PK',
};
function ncesGradesToCanonical(grades) {
  if (!grades) return [];
  const list = Array.isArray(grades)
    ? grades
    : String(grades).split(/[,\s]+/).filter(Boolean);
  if (!list.length) return [];
  const seen = new Set();
  const out = [];
  list.forEach((g) => {
    const s = String(g).trim().toUpperCase();
    let canon = GRADE_ALIASES[s];
    if (!canon) {
      const n = parseInt(s, 10);
      if (!Number.isNaN(n) && n >= 1 && n <= 12) canon = String(n);
      else if (GRADE_ORDER.includes(s)) canon = s;
    }
    if (canon && GRADE_ORDER.includes(canon) && !seen.has(canon)) {
      seen.add(canon);
      out.push(canon);
    }
  });
  return out.sort((a, b) => GRADE_ORDER.indexOf(a) - GRADE_ORDER.indexOf(b));
}

function nearbySchoolIcon({ color, isCharter }) {
  const size = 38;
  const pinPath = 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';
  const schoolPath = 'M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z';
  const iconPath = isCharter ? schoolPath : pinPath;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" style="filter: drop-shadow(0 1px 3px rgba(0,0,0,0.45))">
      <path d="${iconPath}" fill="${color}" stroke="#ffffff" stroke-width="2" paint-order="stroke"/>
    </svg>
  `;
  return L.divIcon({
    html: svg,
    className: 'nearby-school-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size / 2],
  });
}

const SELECTED_SCHOOL_STAR_COLOR = '#facc15';
const SELECTED_SCHOOL_STAR_ICON = (() => {
  const size = 36;
  const starPath = 'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z';
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" style="filter: drop-shadow(0 1px 3px rgba(0,0,0,0.55))">
      <path d="${starPath}" fill="${SELECTED_SCHOOL_STAR_COLOR}" stroke="#1f2937" stroke-width="1.5" paint-order="stroke"/>
    </svg>
  `;
  return L.divIcon({
    html: svg,
    className: 'selected-school-star-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
})();

// Maps each grade to the single ESRI age array index that represents typical enrollment age.
// (Index 0 = age 4, index 1 = age 5, …, index 13 = age 17.)
const GRADE_TO_AGE_IDX = {
  PK: [0], K: [1], '1': [2], '2': [3], '3': [4], '4': [5],
  '5': [6], '6': [7], '7': [8], '8': [9], '9': [10],
  '10': [11], '11': [12], '12': [13],
};

const QUADRANT_LABELS = ['NW', 'NE', 'SW', 'SE'];

// ─── Map Helpers ──────────────────────────────────────────────────────────────

/** Fits the map to given bounds whenever they change. */
function MapFitBounds({ bounds, disabled }) {
  const map = useMap();
  useEffect(() => {
    if (disabled || !bounds) return;
    map.fitBounds(bounds, { padding: [30, 30], animate: true });
  }, [map, bounds, disabled]);
  return null;
}

function MapReady({ mapRef }) {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);
  return null;
}

function MapFitSearched({ schoolLocation, searchedLocation }) {
  const map = useMap();
  useEffect(() => {
    if (!searchedLocation) return;
    const points = [[searchedLocation.lat, searchedLocation.lng]];
    if (schoolLocation) points.push([schoolLocation.lat, schoolLocation.lng]);
    map.fitBounds(points, { padding: [50, 50], maxZoom: 15, animate: true });
  }, [map, schoolLocation, searchedLocation]);
  return null;
}

/**
 * Widens the view to reach the expanded portfolio once per scope change. Refitting on
 * every points change would fight the user's zoom as they adjust the nearby filters.
 */
function MapFitPortfolioScope({ scope, points, disabled }) {
  const map = useMap();
  const fittedScopeRef = useRef(null);
  useEffect(() => {
    if (scope === PORTFOLIO_SCOPES.DRIVE_TIME) {
      fittedScopeRef.current = null;
      return;
    }
    if (disabled || points.length === 0 || fittedScopeRef.current === scope) return;
    fittedScopeRef.current = scope;
    map.fitBounds(points, { padding: [40, 40], animate: true });
  }, [map, scope, points, disabled]);
  return null;
}

function NearbySchoolMapMarker({ school, selected, popupNonce, color, isCharter, isOnTeam, metrics }) {
  const markerRef = useRef(null);
  const gradeRange = getSchoolGradeRange(school);
  useEffect(() => {
    if (!selected) return;
    const marker = markerRef.current;
    if (!marker) return;
    marker.openPopup();
  }, [selected, popupNonce]);
  return (
    <Marker
      ref={markerRef}
      position={[school.latitude, school.longitude]}
      icon={nearbySchoolIcon({ color, isCharter })}
      zIndexOffset={selected ? 1200 : 400}
    >
      <Popup>
        <div style={{ minWidth: 140 }}>
          <div style={{ fontWeight: 700, fontSize: '0.8rem', marginBottom: 4 }}>
            {school.name}
          </div>
          <div style={{ fontSize: '0.72rem', color: '#475569' }}>
            {isCharter ? 'Charter' : 'District'}
            {gradeRange ? ` · ${gradeRange}` : ''}
          </div>
          {metrics && (
            <div style={{ marginTop: 6, fontSize: '0.72rem', color: '#334155', lineHeight: 1.45 }}>
              <div>{metrics.enrollmentLine}</div>
              <div>{metrics.trendLine}</div>
              <div>{metrics.academicLine}</div>
            </div>
          )}
          {isOnTeam && (
            <div style={{
              marginTop: 6,
              display: 'inline-block',
              fontSize: '0.65rem',
              fontWeight: 700,
              color: '#4f46e5',
              background: '#eef2ff',
              borderRadius: 4,
              padding: '1px 6px',
            }}
            >
              Same portfolio
            </div>
          )}
        </div>
      </Popup>
    </Marker>
  );
}

const DISTRICT_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
  '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B88B', '#ABEBC6',
  '#FAD7A0', '#D7BDE2', '#A9CCE3', '#F9E79F', '#A3E4D7',
  '#E6B0AA', '#D5DBDB', '#AED6F1', '#F5CBA7', '#A9DFBF',
];

const DISTRICT_DEFAULT_STYLE = {
  color: '#555',
  weight: 2,
  opacity: 0.6,
  dashArray: '8, 4',
  fillOpacity: 0,
};

function getDistrictColor(districtName) {
  let hash = 0;
  for (let i = 0; i < districtName.length; i++) {
    hash = districtName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return DISTRICT_COLORS[Math.abs(hash) % DISTRICT_COLORS.length];
}

function districtDisplayName(feature) {
  return feature?.properties?.NAME
    || feature?.properties?.name
    || feature?.properties?.DISTRICT
    || 'Unknown District';
}

function districtPolygonStyle(feature, selectedName) {
  const districtName = districtDisplayName(feature);
  const isSelected = Boolean(selectedName) && districtName === selectedName;
  const dimOthers = Boolean(selectedName) && !isSelected;
  if (isSelected) {
    const color = getDistrictColor(districtName);
    return {
      color,
      weight: 3,
      opacity: 0.95,
      dashArray: null,
      fillColor: color,
      fillOpacity: 0.28,
    };
  }
  return {
    ...DISTRICT_DEFAULT_STYLE,
    opacity: dimOthers ? 0.22 : DISTRICT_DEFAULT_STYLE.opacity,
    weight: dimOthers ? 1 : DISTRICT_DEFAULT_STYLE.weight,
  };
}

/** Dashed district polygons matching Geographic Explorer nearby maps. */
function DistrictBoundariesLayer({ data, schoolUuid, selectedName }) {
  const onEachFeature = (feature, layer) => {
    const districtName = districtDisplayName(feature);
    const baseColor = getDistrictColor(districtName);

    layer.bindTooltip(districtName, {
      permanent: false,
      sticky: false,
      direction: 'center',
      className: 'district-tooltip',
      opacity: 0.9,
      interactive: false,
    });

    layer.on('click', (e) => {
      L.DomEvent.stop(e);
      layer.closeTooltip();
    });

    layer.on('mouseover', (e) => {
      layer.setStyle({
        fillColor: baseColor,
        fillOpacity: selectedName && districtName === selectedName ? 0.38 : 0.3,
        weight: 3,
        opacity: 0.8,
        dashArray: null,
      });
      layer.openTooltip(e.latlng);
    });

    layer.on('mouseout', () => {
      layer.setStyle(districtPolygonStyle(feature, selectedName));
      layer.closeTooltip();
    });
  };

  return (
    <GeoJSON
      key={`district-boundaries-${schoolUuid || 'default'}-${selectedName || 'all'}-${data?.features?.length || 0}`}
      data={data}
      style={(feature) => districtPolygonStyle(feature, selectedName)}
      onEachFeature={onEachFeature}
      pane="overlayPane"
    />
  );
}

function LegendItem({ swatch, label, labelSx }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
      {swatch}
      <Typography variant="caption" sx={{ fontSize: '0.68rem', lineHeight: 1.2, ...labelSx }}>
        {label}
      </Typography>
    </Box>
  );
}

function CatchmentMapLegend({
  searchedLocation,
  showNearbySchools,
  showTeamDriveTimes,
  hasExpandedPortfolio,
  hasNewToSchoolTags,
  showAmenities,
  customPlaceLegendItems,
}) {
  return (
    <Paper elevation={3} sx={{ pointerEvents: 'auto', px: 1.25, py: 1, minWidth: 148 }}>
      <Stack spacing={0.6}>
        <LegendItem
          label="School"
          swatch={
            <Box
              component="span"
              sx={{
                color: SELECTED_SCHOOL_STAR_COLOR,
                fontSize: 13,
                lineHeight: 1,
                textShadow: '0 0 1px #1f2937, 0 1px 2px rgba(0,0,0,0.35)',
              }}
            >
              ★
            </Box>
          }
        />
        {searchedLocation && (
          <LegendItem
            label="Searched address"
            swatch={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#2563eb', flexShrink: 0 }} />}
          />
        )}
        {showNearbySchools && (
          <>
            <LegendItem
              label="Charter"
              labelSx={{ fontWeight: 700, color: NEARBY_CHARTER_COLOR }}
              swatch={
                <Box sx={{
                  width: 10, height: 10, borderRadius: '50%', bgcolor: NEARBY_CHARTER_COLOR, flexShrink: 0,
                  boxShadow: '0 0 0 2px #fff, 0 1px 3px rgba(0,0,0,0.35)',
                }}
                />
              }
            />
            <LegendItem
              label="District"
              labelSx={{ fontWeight: 700, color: NEARBY_DISTRICT_COLOR }}
              swatch={
                <Box sx={{
                  width: 10, height: 10, borderRadius: '50%', bgcolor: NEARBY_DISTRICT_COLOR, flexShrink: 0,
                  boxShadow: '0 0 0 2px #fff, 0 1px 3px rgba(0,0,0,0.35)',
                }}
                />
              }
            />
          </>
        )}
        {hasExpandedPortfolio && (
          <LegendItem
            label="Portfolio (outside drive time)"
            labelSx={{ fontWeight: 700, color: TEAM_DRIVE_TIME_COLOR }}
            swatch={
              <Box sx={{
                width: 10, height: 10, borderRadius: '50%', bgcolor: TEAM_DRIVE_TIME_COLOR, flexShrink: 0,
                boxShadow: '0 0 0 2px #fff, 0 1px 3px rgba(0,0,0,0.35)',
              }}
              />
            }
          />
        )}
        {showTeamDriveTimes && (
          <LegendItem
            label="Portfolio catchment"
            labelSx={{ fontWeight: 700, color: TEAM_DRIVE_TIME_COLOR }}
            swatch={
              <Box sx={{ width: 14, height: 0, borderTop: `2.5px dashed ${TEAM_DRIVE_TIME_COLOR}`, flexShrink: 0 }} />
            }
          />
        )}
        {hasNewToSchoolTags ? (
          <>
            <LegendItem
              label="New"
              swatch={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: NEW_STUDENT_COLOR, opacity: 0.9, flexShrink: 0 }} />}
            />
            <LegendItem
              label="Existing"
              swatch={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: EXISTING_STUDENT_COLOR, opacity: 0.9, flexShrink: 0 }} />}
            />
            <LegendItem
              label="Unknown"
              swatch={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: UNKNOWN_STUDENT_COLOR, opacity: 0.9, flexShrink: 0 }} />}
            />
          </>
        ) : (
          <LegendItem
            label="Student address"
            swatch={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: EXISTING_STUDENT_COLOR, opacity: 0.8, flexShrink: 0 }} />}
          />
        )}
        {showAmenities && (
          <>
            <LegendItem
              label="Library"
              swatch={<Box sx={{ width: 10, height: 10, borderRadius: '4px', bgcolor: LIBRARY_COLOR, flexShrink: 0 }} />}
            />
            <LegendItem
              label="Community center"
              swatch={<Box sx={{ width: 10, height: 10, borderRadius: '4px', bgcolor: COMMUNITY_CENTER_COLOR, flexShrink: 0 }} />}
            />
          </>
        )}
        {(customPlaceLegendItems || []).map((item) => (
          <LegendItem
            key={item.label}
            label={item.label}
            swatch={
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: item.color,
                  flexShrink: 0,
                  boxShadow: '0 0 0 1px #fff, 0 1px 2px rgba(0,0,0,0.25)',
                }}
              />
            }
          />
        ))}
      </Stack>
    </Paper>
  );
}

/** Parse an ESRI-format drive time polygon (rings) into a GeoJSON Feature. */
function parseDriveTimePolygon(rawPolygon) {
  try {
    let polygonData = rawPolygon;
    if (typeof polygonData === 'string') {
      polygonData = JSON.parse(polygonData);
      if (typeof polygonData === 'string') {
        polygonData = JSON.parse(polygonData);
      }
    }
    const rings = polygonData?.rings;
    if (!rings?.length) return null;
    return {
      type: 'Feature',
      properties: {},
      geometry: { type: 'Polygon', coordinates: rings },
    };
  } catch {
    return null;
  }
}

/**
 * Count students (cumulative) within each drive-time polygon using Turf.
 * Also computes markerZones: the innermost zone assignment for each marker.
 *
 * Cumulative means: a student in the 5-min ring is counted in 5, 10, and 15.
 * Innermost zone is used for map highlighting — it identifies which exclusive
 * ring a student belongs to (5-min, 10-min, 15-min, or outside).
 */
function countStudentsInPolygons(markers, geoJsonFeatures) {
  const counts = Object.fromEntries(DRIVE_TIMES.map((dt) => [dt, 0]));
  let outside = 0;
  const markerZones = [];

  markers.forEach(({ lat, lng }) => {
    const pt = turfPoint([lng, lat]);
    let innermostZone = 'outside';

    // Check smallest ring first to find the innermost zone
    for (const dt of DRIVE_TIMES) {
      if (geoJsonFeatures[dt]) {
        try {
          const poly = turfPolygon(geoJsonFeatures[dt].geometry.coordinates);
          if (booleanPointInPolygon(pt, poly)) {
            innermostZone = dt;
            break;
          }
        } catch {
          // skip malformed polygon
        }
      }
    }

    markerZones.push(innermostZone);

    if (innermostZone === 'outside') {
      outside++;
    } else {
      // Cumulative: count for all rings >= the innermost zone
      DRIVE_TIMES.forEach((dt) => {
        if (dt >= innermostZone) {
          counts[dt]++;
        }
      });
    }
  });

  return { counts, outside, markerZones };
}

/** Returns a human-readable age label for a Set of ESRI age array indices.
 *  Uses a range (e.g. "ages 5–10") only when the indices are fully consecutive;
 *  otherwise lists each age individually (e.g. "ages 7, 9"). */
function getAgeRangeLabel(indexSet) {
  if (!indexSet || indexSet.size === 0) return 'ages 5–17';
  const sorted = [...indexSet].sort((a, b) => a - b);
  const ages = sorted.map((i) => i + 4);
  if (ages.length === 1) return `age ${ages[0]}`;
  const isConsecutive = sorted.every((idx, pos) => pos === 0 || idx === sorted[pos - 1] + 1);
  if (isConsecutive) return `ages ${ages[0]}–${ages[ages.length - 1]}`;
  return `ages ${ages.join(', ')}`;
}

/**
 * Whether a marker should be shown given the selected zone and display mode.
 * Cumulative: selecting the 10-min zone shows students in both the 5 and 10-min rings.
 * Ring (exclusive): selecting the 10-min ring shows only students between 5 and 10 min.
 */
function driveTimePolygonStyle(dt, selectedZone) {
  const color = driveTimeColors[dt];
  const isFocus = selectedZone === dt;
  const dimOuter = selectedZone != null && selectedZone !== 'outside' && !isFocus;
  return {
    color,
    weight: isFocus ? 3.5 : 2,
    opacity: dimOuter ? 0.2 : 0.9,
    fillColor: color,
    fillOpacity: isFocus ? 0.22 : dimOuter ? 0.03 : 0.08,
  };
}

function isMarkerHighlighted(innermostZone, selectedZone, cumulative = true) {
  if (selectedZone === null) return true;
  if (selectedZone === 'outside') return innermostZone === 'outside';
  if (cumulative) {
    return innermostZone !== 'outside' && innermostZone <= selectedZone;
  }
  return innermostZone === selectedZone;
}

function getSchoolGradeRange(school) {
  return school?.grade_range || school?.grade_range || '';
}

function schoolSharesGradesWithTarget(school, targetGrades) {
  if (!targetGrades?.length) return false;
  const range = getSchoolGradeRange(school);
  if (!range) return false;
  return hasGradeOverlap(range, toExplorerGrades(targetGrades));
}


// ─── Status Icon ─────────────────────────────────────────────────────────────

function StepStatusIcon({ status }) {
  if (status === 'loading') return <CircularProgress size={18} />;
  if (status === 'done') return <CheckCircleIcon color="success" fontSize="small" />;
  if (status === 'skipped') return <BoltIcon color="primary" fontSize="small" />;
  if (status === 'error') return <ErrorOutlineIcon color="error" fontSize="small" />;
  return <HourglassEmptyIcon color="disabled" fontSize="small" />;
}

// ─── Main Component ───────────────────────────────────────────────────────────

const StudentCatchmentTool = () => {
  const trackUsage = useFeatureUsage(PRODUCT_AREAS.STUDENT_CATCHMENT_EXPLORER);
  const { enqueueSnackbar } = useSnackbar();
  const { setToolAndSection } = useInsight();
  const { schools } = useTeamSchoolCache();
  const { selectedSchool: storeSchool } = useSchoolStore();

  // Stepper
  const [activeStep, setActiveStep] = useState(0);

  // Phase 1 — school selection (assigned schools only)
  const [selectedSchool, setSelectedSchool] = useState(null);
  const initialSchoolLoadRef = useRef(false);

  useEffect(() => {
    setToolAndSection('student-catchment-explorer', null);
  }, [setToolAndSection]);

  // Phase 2 — CSV
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvError, setCsvError] = useState(null);

  // source: null | 'api' | 'db_cache' | 'session' | 'persistent'
  const [geocodeSource, setGeocodeSource] = useState(null);
  const [geocodeCacheStats, setGeocodeCacheStats] = useState(null);

  // Persistent entries check
  const [checkingExistingData, setCheckingExistingData] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [clearingData, setClearingData] = useState(false);

  // Phase 3 — processing
  const [processing, setProcessing] = useState(false);
  const [processingSteps, setProcessingSteps] = useState({ ...INITIAL_PROCESSING_STEPS });

  // Phase 3 — results
  const [studentMarkers, setStudentMarkers] = useState([]);
  const [failedGeocode, setFailedGeocode] = useState(0);
  const [driveTimeGeoJSON, setDriveTimeGeoJSON] = useState({});
  const [schoolLocation, setSchoolLocation] = useState(null);
  const [catchmentCounts, setCatchmentCounts] = useState(null);
  const [visibleDriveTimes, setVisibleDriveTimes] = useState([5, 10, 15]);

  // Zone highlighting
  const [selectedZone, setSelectedZone] = useState(null);
  const [markerZones, setMarkerZones] = useState([]);

  // Map display modes
  const [clusterMarkers, setClusterMarkers] = useState(false);
  const [districtBoundaries, setDistrictBoundaries] = useState(null);
  const [showDistrictBoundaries, setShowDistrictBoundaries] = useState(true);

  // Nearby schools overlay (school grades, not student PII)
  const [showNearbySchools, setShowNearbySchools] = useState(false);
  const [nearbySchools, setNearbySchools] = useState([]);
  const [nearbySchoolsLoading, setNearbySchoolsLoading] = useState(false);
  const [selectedNearbySchoolUuid, setSelectedNearbySchoolUuid] = useState(null);
  const [nearbyPopupNonce, setNearbyPopupNonce] = useState(0);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [nearbyGovernance, setNearbyGovernance] = useState(['Charter', 'District']);
  const [nearbyGrades, setNearbyGrades] = useState([]);
  const [targetSchoolGrades, setTargetSchoolGrades] = useState([]);
  const [sameTeamOnly, setSameTeamOnly] = useState(false);
  const [portfolioScope, setPortfolioScope] = useState(PORTFOLIO_SCOPES.DRIVE_TIME);
  const teamSchoolUuids = useMemo(
    () => new Set((schools || []).map((school) => school.uuid).filter(Boolean)),
    [schools]
  );
  const hasPortfolioSchools = teamSchoolUuids.size > 0;
  const targetCountyLabel = (selectedSchool?.county || '').trim();
  const targetCountyKnown = Boolean(normalizeCounty(targetCountyLabel));
  const [showTeamDriveTimes, setShowTeamDriveTimes] = useState(false);
  const [teamDriveTimeOverlays, setTeamDriveTimeOverlays] = useState({});
  const [teamDriveTimesLoading, setTeamDriveTimesLoading] = useState(false);
  const [nearbyEnrollmentByUuid, setNearbyEnrollmentByUuid] = useState({});
  const [basemap, setBasemap] = useState('streets');
  const [showAmenities, setShowAmenities] = useState(false);
  const [amenityPlaces, setAmenityPlaces] = useState([]);
  const [amenitiesLoading, setAmenitiesLoading] = useState(false);
  const amenityBboxKeyRef = useRef('');
  const [newToSchoolFilter, setNewToSchoolFilter] = useState('all');
  const [customPlaces, setCustomPlaces] = useState([]);
  const [showCustomPlaces, setShowCustomPlaces] = useState(true);
  const [placesUploading, setPlacesUploading] = useState(false);
  const [placeTypeFilter, setPlaceTypeFilter] = useState(null);
  const [placesClearConfirmOpen, setPlacesClearConfirmOpen] = useState(false);
  const [placesUploadOpen, setPlacesUploadOpen] = useState(false);
  const placesFileInputRef = useRef(null);
  const [tourDismissed, setTourDismissed] = useState(() => readCatchmentTourDismissed());
  const [tourReplay, setTourReplay] = useState(false);
  const [tourStep, setTourStep] = useState(0);

  // Cumulative vs. ring (exclusive) breakdown mode
  const [cumulativeMode, setCumulativeMode] = useState(true);

  // Quadrant filter (null = all, Set<'NW'|'NE'|'SW'|'SE'> = only those quadrants)
  const [selectedQuadrants, setSelectedQuadrants] = useState(null);
  const [showQuadrantLines, setShowQuadrantLines] = useState(false);

  // Full ESRI market-share response (ages 4–17 per drive-time zone)
  const [esriMarketData, setEsriMarketData] = useState(null);

  // One-pager export + address search
  const mapInstanceRef = useRef(null);
  const mapCaptureRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [recommendationCollapsed, setRecommendationCollapsed] = useState(false);
  const [recommendationDismissed, setRecommendationDismissed] = useState(false);
  const [controlsOpen, setControlsOpen] = useState(false);
  const [resultsTableTab, setResultsTableTab] = useState('nearby');
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSearching, setAddressSearching] = useState(false);
  const [searchedLocation, setSearchedLocation] = useState(null);

  const showingTour = shouldShowCatchmentTour({
    dismissed: tourDismissed,
    replaying: tourReplay,
    hasResults: activeStep === 2 && Boolean(schoolLocation),
  });

  useEffect(() => {
    setRecommendationCollapsed(false);
    setRecommendationDismissed(false);
  }, [selectedSchool?.uuid]);

  useEffect(() => {
    if (!showingTour) return;
    if (CATCHMENT_TOUR_STEPS[tourStep]?.expandControls) {
      setControlsOpen(true);
    }
  }, [showingTour, tourStep]);

  // ── Derived ───────────────────────────────────────────────────────────────

  const filteredNearbySchools = useMemo(() => {
    if (!showNearbySchools || nearbySchools.length === 0) return [];
    if (nearbyGovernance.length === 0) return [];
    let list = filterSchools(
      nearbySchools,
      {
        selectedGrades: toExplorerGrades(nearbyGrades),
        governanceFilter: nearbyGovernance,
        schoolTypeFilter: [],
      },
      null
    );
    if (sameTeamOnly) {
      if (!teamSchoolUuids) return [];
      list = list.filter((s) => teamSchoolUuids.has(s.uuid));
    }
    // Portfolio campuses only belong in this view when they share the filtered grades
    // (defaults to the target school's grades; adding a feeder grade includes those schools).
    const gradesForPortfolio = nearbyGrades.length > 0 ? nearbyGrades : targetSchoolGrades;
    if (teamSchoolUuids && gradesForPortfolio.length > 0) {
      list = list.filter((s) => (
        !teamSchoolUuids.has(s.uuid) || schoolSharesGradesWithTarget(s, gradesForPortfolio)
      ));
    }
    return list;
  }, [
    showNearbySchools, nearbySchools, nearbyGovernance, nearbyGrades,
    sameTeamOnly, teamSchoolUuids, targetSchoolGrades,
  ]);

  // Portfolio campuses the scope toggle reaches beyond the drive-time rings. Kept separate
  // from the filtered set so tweaking governance or grades does not refetch enrollment.
  const portfolioScopeCandidates = useMemo(() => {
    if (!showNearbySchools || !hasPortfolioSchools) return [];
    return portfolioSchoolsOutsideDriveTimes({
      portfolioSchools: schools,
      scope: portfolioScope,
      targetSchool: selectedSchool,
      nearbyUuids: new Set((nearbySchools || []).map((s) => s.uuid)),
    });
  }, [
    showNearbySchools, hasPortfolioSchools, portfolioScope, schools, selectedSchool, nearbySchools,
  ]);

  // Governance and grade rules match filteredNearbySchools so both sets react to the same filters.
  const expandedPortfolioSchools = useMemo(() => {
    if (portfolioScopeCandidates.length === 0 || nearbyGovernance.length === 0) return [];
    let list = filterSchools(
      portfolioScopeCandidates,
      {
        selectedGrades: toExplorerGrades(nearbyGrades),
        governanceFilter: nearbyGovernance,
        schoolTypeFilter: [],
      },
      null
    );
    const gradesForPortfolio = nearbyGrades.length > 0 ? nearbyGrades : targetSchoolGrades;
    if (gradesForPortfolio.length > 0) {
      list = list.filter((s) => schoolSharesGradesWithTarget(s, gradesForPortfolio));
    }
    return list;
  }, [portfolioScopeCandidates, nearbyGovernance, nearbyGrades, targetSchoolGrades]);

  // Portfolio campuses the grade filter is holding back. Surfaced so an empty portfolio
  // list reads as a filter choice rather than missing data.
  const portfolioHiddenByGrades = useMemo(() => {
    if (!showNearbySchools || !hasPortfolioSchools) return 0;
    const gradesForPortfolio = nearbyGrades.length > 0 ? nearbyGrades : targetSchoolGrades;
    if (gradesForPortfolio.length === 0) return 0;
    const pool = [
      ...(nearbySchools || []).filter((s) => s.uuid && teamSchoolUuids.has(s.uuid)),
      ...portfolioScopeCandidates,
    ];
    return pool.filter((s) => (
      s.uuid !== selectedSchool?.uuid
      && !schoolSharesGradesWithTarget(s, gradesForPortfolio)
    )).length;
  }, [
    showNearbySchools, hasPortfolioSchools, nearbySchools, portfolioScopeCandidates,
    nearbyGrades, targetSchoolGrades, teamSchoolUuids, selectedSchool?.uuid,
  ]);

  // Keep the control honest when a newly selected campus has no county on file.
  useEffect(() => {
    if (portfolioScope === PORTFOLIO_SCOPES.COUNTY && !targetCountyKnown) {
      setPortfolioScope(PORTFOLIO_SCOPES.DRIVE_TIME);
    }
  }, [portfolioScope, targetCountyKnown]);

  const nearbyMetricGrades = useMemo(
    () => toExplorerGrades(nearbyGrades.length > 0 ? nearbyGrades : targetSchoolGrades),
    [nearbyGrades, targetSchoolGrades]
  );

  // Outer ring currently shown. Portfolio campuses beyond it have no catchment overlap.
  const maxVisibleDriveTime = useMemo(
    () => Math.max(...(visibleDriveTimes.length ? visibleDriveTimes : [15])),
    [visibleDriveTimes]
  );

  const hasNewToSchoolTags = useMemo(
    () => studentMarkers.some((m) => m.newToSchool === true || m.newToSchool === false),
    [studentMarkers]
  );

  const controlsSummary = useMemo(() => {
    const chips = [];
    if (visibleDriveTimes.length > 0) {
      chips.push(`${visibleDriveTimes.join('/')}-min`);
    }
    if (clusterMarkers) chips.push('Clustered');
    if (selectedQuadrants && selectedQuadrants.size > 0) {
      chips.push([...selectedQuadrants].sort().join(', '));
    }
    if (hasNewToSchoolTags && newToSchoolFilter !== 'all') {
      chips.push(newToSchoolFilter === 'new' ? 'New students' : 'Existing students');
    }
    if (showAmenities) chips.push('Libraries');
    if (showCustomPlaces && customPlaces.length > 0) {
      chips.push(`${customPlaces.length} place${customPlaces.length === 1 ? '' : 's'}`);
    }
    if (showNearbySchools && portfolioScope === PORTFOLIO_SCOPES.COUNTY) {
      chips.push('Portfolio: county');
    }
    if (showNearbySchools && portfolioScope === PORTFOLIO_SCOPES.ALL) {
      chips.push('Portfolio: all');
    }
    return chips;
  }, [
    visibleDriveTimes, clusterMarkers, selectedQuadrants, hasNewToSchoolTags,
    showNearbySchools, portfolioScope,
    newToSchoolFilter, showAmenities, showCustomPlaces, customPlaces.length,
  ]);

  const neighborhoodSummary = useMemo(
    () => summarizeNeighborhoods(studentMarkers),
    [studentMarkers]
  );

  const districtSummary = useMemo(
    () => summarizeDistricts(studentMarkers, districtBoundaries),
    [studentMarkers, districtBoundaries]
  );

  const placeTypeOptions = useMemo(() => {
    const seen = new Map();
    customPlaces.forEach((place) => {
      if (!seen.has(place.typeLabel)) seen.set(place.typeLabel, place.kind);
    });
    return [...seen.entries()].map(([label, kind]) => ({ label, kind }));
  }, [customPlaces]);

  const visibleCustomPlaces = useMemo(() => {
    if (!showCustomPlaces) return [];
    if (!placeTypeFilter || placeTypeFilter.size === 0) return customPlaces;
    return customPlaces.filter((place) => placeTypeFilter.has(place.typeLabel));
  }, [showCustomPlaces, customPlaces, placeTypeFilter]);

  const customPlaceLegendItems = useMemo(() => {
    const seen = new Map();
    visibleCustomPlaces.forEach((place) => {
      if (!seen.has(place.typeLabel)) {
        seen.set(place.typeLabel, placeTypeColor(place.kind));
      }
    });
    return [...seen.entries()].map(([label, color]) => ({ label, color }));
  }, [visibleCustomPlaces]);

  const nearbySchoolTableRows = useMemo(() => {
    if (!showNearbySchools) return [];
    if (filteredNearbySchools.length === 0 && expandedPortfolioSchools.length === 0) return [];
    const buildRow = (s, isExpanded) => {
      const isCharter = s.charter === 'Yes' || s.is_charter === true;
      const grades = getSchoolGradeRange(s);
      const isTeam = Boolean(teamSchoolUuids?.has(s.uuid));
      const catchmentDt = isExpanded
        ? null
        : getInnermostDriveTime(s.latitude, s.longitude, driveTimeGeoJSON);
      const metrics = nearbySchoolMetrics(
        nearbyEnrollmentByUuid[s.uuid],
        nearbyMetricGrades
      );
      return {
        uuid: s.uuid,
        name: s.name || 'Unnamed school',
        type: isCharter ? 'Charter' : 'District',
        grades,
        isTeam,
        isExpanded,
        catchmentDt,
        lat: s.latitude,
        lng: s.longitude,
        milesFromTarget: schoolLocation
          ? haversineMiles(schoolLocation.lat, schoolLocation.lng, s.latitude, s.longitude)
          : null,
        enrollment: metrics.enrollment,
        enrollmentLabel: metrics.enrollmentLabel,
        trendPct: metrics.trendPct,
        trendLabel: metrics.trendLabel,
        academicLabel: metrics.academicLabel,
        academicMetric: metrics.academicMetric,
      };
    };
    return [
      ...filteredNearbySchools.map((s) => buildRow(s, false)),
      ...expandedPortfolioSchools.map((s) => buildRow(s, true)),
    ].sort((a, b) => {
      // Drive-time schools first (portfolio, then by ring); expanded portfolio trails by distance.
      if (a.isExpanded !== b.isExpanded) return a.isExpanded ? 1 : -1;
      if (a.isExpanded) {
        const miles = (m) => (m == null ? Number.POSITIVE_INFINITY : m);
        if (miles(a.milesFromTarget) !== miles(b.milesFromTarget)) {
          return miles(a.milesFromTarget) - miles(b.milesFromTarget);
        }
        return a.name.localeCompare(b.name);
      }
      if (a.isTeam !== b.isTeam) return a.isTeam ? -1 : 1;
      const rank = (dt) => (dt == null ? 99 : dt);
      if (rank(a.catchmentDt) !== rank(b.catchmentDt)) {
        return rank(a.catchmentDt) - rank(b.catchmentDt);
      }
      return a.name.localeCompare(b.name);
    });
  }, [
    showNearbySchools, filteredNearbySchools, expandedPortfolioSchools, teamSchoolUuids,
    driveTimeGeoJSON, schoolLocation, nearbyEnrollmentByUuid, nearbyMetricGrades,
  ]);

  useEffect(() => {
    const nearbyAvailable = showNearbySchools && (nearbySchoolsLoading || nearbySchoolTableRows.length > 0);
    const hoodsAvailable = neighborhoodSummary.length > 0;
    const districtsAvailable = districtSummary.length > 0;
    const available = {
      nearby: nearbyAvailable,
      neighborhoods: hoodsAvailable,
      districts: districtsAvailable,
    };
    if (available[resultsTableTab]) return;
    if (nearbyAvailable) setResultsTableTab('nearby');
    else if (hoodsAvailable) setResultsTableTab('neighborhoods');
    else if (districtsAvailable) setResultsTableTab('districts');
  }, [
    showNearbySchools, nearbySchoolsLoading, nearbySchoolTableRows.length,
    neighborhoodSummary.length, districtSummary.length, resultsTableTab,
  ]);

  useEffect(() => {
    if (!selectedNearbySchoolUuid) return;
    const stillVisible = (s) => s.uuid === selectedNearbySchoolUuid;
    if (!filteredNearbySchools.some(stillVisible) && !expandedPortfolioSchools.some(stillVisible)) {
      setSelectedNearbySchoolUuid(null);
    }
  }, [filteredNearbySchools, expandedPortfolioSchools, selectedNearbySchoolUuid]);

  const teamNearbyCandidates = useMemo(() => {
    if (!showNearbySchools || !showTeamDriveTimes || !teamSchoolUuids) return [];
    return filteredNearbySchools.filter(
      (s) => s.uuid && s.uuid !== selectedSchool?.uuid && teamSchoolUuids.has(s.uuid)
    );
  }, [
    showNearbySchools, showTeamDriveTimes, teamSchoolUuids,
    filteredNearbySchools, selectedSchool?.uuid,
  ]);

  // Exclusive ring counts derived from markerZones (no extra backend call needed)
  const ringCounts = useMemo(() => {
    const counts = Object.fromEntries(DRIVE_TIMES.map((dt) => [dt, 0]));
    markerZones.forEach((z) => {
      if (z !== 'outside' && counts[z] !== undefined) counts[z]++;
    });
    return counts;
  }, [markerZones]);

  // Recomputed zone counts when quadrant filters are active
  const filteredStats = useMemo(() => {
    const hasQuadrantFilter = selectedQuadrants && selectedQuadrants.size > 0;
    if (!hasQuadrantFilter) return null;
    if (!catchmentCounts || markerZones.length === 0) return null;

    const counts = Object.fromEntries(DRIVE_TIMES.map((dt) => [dt, 0]));
    const ringCts = Object.fromEntries(DRIVE_TIMES.map((dt) => [dt, 0]));
    let outside = 0;
    let total = 0;

    studentMarkers.forEach((marker, i) => {
      const zone = markerZones[i];
      if (hasQuadrantFilter && schoolLocation) {
        const isNorth = marker.lat >= schoolLocation.lat;
        const isEast = marker.lng >= schoolLocation.lng;
        const q = isNorth ? (isEast ? 'NE' : 'NW') : (isEast ? 'SE' : 'SW');
        if (!selectedQuadrants.has(q)) return;
      }
      total++;
      if (zone === 'outside') {
        outside++;
      } else {
        DRIVE_TIMES.forEach((dt) => { if (dt >= zone) counts[dt]++; });
        ringCts[zone]++;
      }
    });

    return { counts, ringCounts: ringCts, outside, total };
  }, [studentMarkers, markerZones, selectedQuadrants, schoolLocation, catchmentCounts]);

  // Auto-generated insight headlines
  const insights = useMemo(() => {
    if (!catchmentCounts || !schoolLocation || studentMarkers.length === 0 || markerZones.length === 0) return null;

    const total = studentMarkers.length;

    // ── 1. Dominant exclusive ring ─────────────────────────────────────────
    const allZoneCounts = {
      ...Object.fromEntries(DRIVE_TIMES.map((dt) => [dt, ringCounts[dt]])),
      outside: catchmentCounts.outside,
    };
    const [dominantZoneKey, dominantCount] = Object.entries(allZoneCounts)
      .sort((a, b) => b[1] - a[1])[0];
    const dominantZone = dominantZoneKey === 'outside' ? 'outside' : Number(dominantZoneKey);
    const dominantPct = Math.round((dominantCount / total) * 100);
    const dominantLabel =
      dominantZone === 'outside' ? 'beyond 15 min' : RING_LABELS[dominantZone].toLowerCase();
    const dominantColor =
      dominantZone === 'outside' ? '#9ca3af' : driveTimeColors[dominantZone];

    // ── 2. Median drive-time ring (ordinal, not routed minutes) ────────────
    const medianZone = medianDriveZone(markerZones);
    const preciseMedian = medianMinutes(studentMarkers.map((m) => m.driveTimeMinutes));
    const usePreciseMedian = preciseMedian != null;
    const medianLabel = usePreciseMedian
      ? `${Math.round(preciseMedian)} min`
      : medianZone === 'outside'
        ? 'beyond 15 min'
        : RING_LABELS[medianZone];
    const medianColor = usePreciseMedian
      ? colorForMinutes(preciseMedian)
      : medianZone === 'outside'
        ? '#9ca3af'
        : driveTimeColors[medianZone];

    const focusDriveTime = focusDriveTimeFromMedian(
      usePreciseMedian ? preciseMedian : null,
      medianZone,
    );
    const focusAdvice = focusAdviceCopy(focusDriveTime, {
      preciseMinutes: preciseMedian,
      usePreciseMedian,
    });
    const focusAdviceColor =
      focusDriveTime === 'outside' || focusDriveTime == null
        ? '#9ca3af'
        : driveTimeColors[focusDriveTime];

    // ── 3. Geographic quadrant concentration ──────────────────────────────
    const quadrants = { NW: 0, NE: 0, SW: 0, SE: 0 };
    studentMarkers.forEach((m) => {
      const isNorth = m.lat >= schoolLocation.lat;
      const isEast = m.lng >= schoolLocation.lng;
      if (isNorth && isEast) quadrants.NE++;
      else if (isNorth && !isEast) quadrants.NW++;
      else if (!isNorth && isEast) quadrants.SE++;
      else quadrants.SW++;
    });
    const sortedQ = Object.entries(quadrants).sort((a, b) => b[1] - a[1]);
    const avgPerQ = total / 4;
    // Label the top quadrant(s) — call out those meaningfully above average
    const heavyQ = sortedQ.filter(([, c]) => c > avgPerQ * 1.25);
    const geoLabel =
      heavyQ.length >= 2
        ? `${heavyQ[0][0]} and ${heavyQ[1][0]}`
        : sortedQ[0][0];
    const topQPct = Math.round((sortedQ[0][1] / total) * 100);
    const isSpread = sortedQ[0][1] / total < 0.35; // fairly even distribution

    return {
      dominantZone, dominantPct, dominantCount, dominantLabel, dominantColor,
      medianZone, medianLabel, medianColor, usePreciseMedian,
      focusDriveTime, focusAdvice, focusAdviceColor,
      geoLabel, topQPct, isSpread, quadrants,
    };
  }, [
    catchmentCounts, schoolLocation, studentMarkers, markerZones,
    ringCounts,
  ]);

  // ── Assigned school selection ────────────────────────────────────────────

  const selectCatchmentSchool = useCallback((school) => {
    if (!school) return;
    const formatted = { ...school, name: formatSchoolName(school.name) };
    setSelectedSchool(formatted);
    useSchoolStore.getState().selectSchool(formatted);
  }, []);

  const handleSchoolChange = useCallback((event) => {
    const school = findAssignedSchool(schools, event.target.value);
    if (!school) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('school')) {
      params.set('school', school.uuid);
      window.history.replaceState(null, '', `${window.location.pathname}?${params}`);
    }
    selectCatchmentSchool(school);
  }, [schools, selectCatchmentSchool]);

  useEffect(() => {
    if (schools.length === 0 || initialSchoolLoadRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const fromUrl = findAssignedSchool(schools, params.get('school'));
    const fromStore = storeSchool
      ? findAssignedSchool(schools, storeSchool.uuid)
      : null;
    const school = fromUrl || fromStore;
    if (school) selectCatchmentSchool(school);
    initialSchoolLoadRef.current = true;
  }, [schools, storeSchool, selectCatchmentSchool]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const schoolIdFromUrl = params.get('school');
    if (schoolIdFromUrl && schools.length > 0 && selectedSchool?.uuid !== schoolIdFromUrl) {
      const school = findAssignedSchool(schools, schoolIdFromUrl);
      if (school) selectCatchmentSchool(school);
    }
  }, [window.location.search, schools, selectedSchool?.uuid, selectCatchmentSchool]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      initialSchoolLoadRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedSchool?.uuid) {
      setDistrictBoundaries(null);
      return undefined;
    }
    let cancelled = false;
    schoolsApi.getDistrictBoundaries(selectedSchool.uuid)
      .then((data) => {
        if (!cancelled) setDistrictBoundaries(data);
      })
      .catch(() => {
        if (!cancelled) setDistrictBoundaries(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSchool?.uuid]);

  useEffect(() => {
    if (!selectedSchool?.uuid) {
      setTargetSchoolGrades([]);
      setNearbyGrades([]);
      return undefined;
    }
    let cancelled = false;
    schoolsApi.getSchoolById(selectedSchool.uuid)
      .then((school) => {
        if (cancelled) return;
        const canonical = ncesGradesToCanonical(
          school?.grades_offered || school?.grades_offered
        );
        setTargetSchoolGrades(canonical);
        setNearbyGrades(canonical);
      })
      .catch(() => {
        if (!cancelled) {
          setTargetSchoolGrades([]);
          setNearbyGrades([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSchool?.uuid]);

  useEffect(() => {
    if (!showNearbySchools || !selectedSchool?.uuid) {
      if (!showNearbySchools) {
        setNearbySchools([]);
        setNearbySchoolsLoading(false);
      }
      return undefined;
    }
    let cancelled = false;
    setNearbySchoolsLoading(true);
    schoolsApi.getAllNearbySchools(selectedSchool.uuid)
      .then((data) => {
        if (cancelled) return;
        const maxDt = Math.max(...(visibleDriveTimes.length ? visibleDriveTimes : [15]));
        const raw = data?.current?.[maxDt] || data?.current?.[String(maxDt)] || [];
        const gradeData = data?.gradeData || {};
        const seen = new Set();
        const unique = [];
        raw.forEach((s) => {
          if (!s?.uuid || s.uuid === selectedSchool.uuid || seen.has(s.uuid)) return;
          if (s.latitude == null || s.longitude == null) return;
          seen.add(s.uuid);
          const range = s.grade_range || s.grade_range || gradeData[s.uuid]?.current || '';
          unique.push({
            ...s,
            grade_range: range,
          });
        });
        setNearbySchools(unique);
      })
      .catch(() => {
        if (!cancelled) setNearbySchools([]);
      })
      .finally(() => {
        if (!cancelled) setNearbySchoolsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showNearbySchools, selectedSchool?.uuid, visibleDriveTimes]);

  useEffect(() => {
    if (!showNearbySchools || nearbySchools.length === 0 || !selectedSchool?.uuid) {
      if (!showNearbySchools) setNearbyEnrollmentByUuid({});
      return undefined;
    }
    let cancelled = false;
    const uuids = [
      ...nearbySchools.map((s) => s.uuid),
      ...portfolioScopeCandidates.map((s) => s.uuid),
    ];
    schoolsApi.getSchoolsEnrollmentData(uuids, selectedSchool.uuid)
      .then((rows) => {
        if (cancelled) return;
        const next = {};
        (rows || []).forEach((row) => {
          if (row?.uuid) next[row.uuid] = row;
        });
        setNearbyEnrollmentByUuid(next);
      })
      .catch(() => {
        if (!cancelled) setNearbyEnrollmentByUuid({});
      });
    return () => {
      cancelled = true;
    };
  }, [showNearbySchools, nearbySchools, portfolioScopeCandidates, selectedSchool?.uuid]);

  useEffect(() => {
    if (!showAmenities) {
      setAmenitiesLoading(false);
      return undefined;
    }
    const bounds = geojsonBounds(driveTimeGeoJSON[15]) || geojsonBounds(driveTimeGeoJSON[10]);
    if (!bounds) return undefined;
    const key = bboxKey(bounds);
    if (key && key === amenityBboxKeyRef.current) return undefined;

    let cancelled = false;
    setAmenitiesLoading(true);
    apiClient.post('/v2/catchment-amenities', {
      south: bounds.south,
      west: bounds.west,
      north: bounds.north,
      east: bounds.east,
    })
      .then((payload) => {
        if (cancelled) return;
        amenityBboxKeyRef.current = key;
        setAmenityPlaces(parseOverpassAmenities(payload));
      })
      .catch(() => {
        if (!cancelled) {
          setAmenityPlaces([]);
          enqueueSnackbar('Could not load libraries and community centers.', { variant: 'warning' });
        }
      })
      .finally(() => {
        if (!cancelled) setAmenitiesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showAmenities, driveTimeGeoJSON, enqueueSnackbar]);

  useEffect(() => {
    if (!showNearbySchools || !showTeamDriveTimes) {
      setTeamDriveTimeOverlays({});
      setTeamDriveTimesLoading(false);
      return undefined;
    }
    if (!teamSchoolUuids || teamNearbyCandidates.length === 0) {
      setTeamDriveTimeOverlays({});
      setTeamDriveTimesLoading(false);
      return undefined;
    }

    let cancelled = false;
    setTeamDriveTimesLoading(true);
    const candidates = teamNearbyCandidates;

    Promise.allSettled(
      candidates.map(async (school) => {
        const esriData = await apiClient.get(`/v2/school/${school.uuid}/esri-market-share`);
        const polygons = {};
        DRIVE_TIMES.forEach((dt) => {
          const timeData = esriData?.drive_times?.[dt];
          if (timeData?.drive_time_polygon) {
            const feature = parseDriveTimePolygon(timeData.drive_time_polygon);
            if (feature) polygons[dt] = feature;
          }
        });
        return { uuid: school.uuid, name: school.name, polygons };
      })
    ).then((results) => {
      if (cancelled) return;
      const next = {};
      results.forEach((result) => {
        if (result.status !== 'fulfilled') return;
        const { uuid, name, polygons } = result.value;
        if (uuid && polygons && Object.keys(polygons).length > 0) {
          next[uuid] = { name, polygons };
        }
      });
      setTeamDriveTimeOverlays(next);
      setTeamDriveTimesLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [showNearbySchools, showTeamDriveTimes, teamSchoolUuids, teamNearbyCandidates]);

  // ── CSV upload & validation ───────────────────────────────────────────────

  const handleFileUpload = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (!file) return;

      if (!file.name.toLowerCase().endsWith('.csv')) {
        setCsvError('Please upload a .csv file.');
        return;
      }

      setCsvFile(file);
      setCsvError(null);
      setCsvData([]);
      setGeocodeSource(null);
      setGeocodeCacheStats(null);

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => (h || '').trim().toLowerCase().replace(/\s+/g, '_'),
        complete: (results) => {
          const headers = (results.meta.fields || []).map((h) => h.trim().toLowerCase());

          const extraColumns = headers.filter((h) => !ALLOWED_COLUMNS.includes(h));
          if (extraColumns.length > 0) {
            setCsvError(
              `CSV contains disallowed columns: "${extraColumns.join('", "')}". ` +
                `Permitted columns: street, city, state, zip_code, and optional new_to_school.`
            );
            setCsvFile(null);
            return;
          }

          const missingColumns = REQUIRED_COLUMNS.filter((c) => !headers.includes(c));
          if (missingColumns.length > 0) {
            setCsvError(
              `CSV is missing required columns: "${missingColumns.join('", "')}". ` +
                `Required: street, city, state, zip_code.`
            );
            setCsvFile(null);
            return;
          }

          if (results.data.length > MAX_STUDENT_ROWS) {
            setCsvError(
              `CSV exceeds the ${MAX_STUDENT_ROWS}-row limit (${results.data.length} rows found). ` +
                `Please split into smaller batches.`
            );
            setCsvFile(null);
            return;
          }

          const validRows = results.data.filter((row) =>
            REQUIRED_COLUMNS.every((col) => row[col]?.trim())
          );
          const skipped = results.data.length - validRows.length;
          if (skipped > 0) {
            enqueueSnackbar(
              `${skipped} row${skipped > 1 ? 's' : ''} with empty address fields will be skipped.`,
              { variant: 'warning' }
            );
          }

          if (validRows.length === 0) {
            setCsvError('No valid rows found in the CSV after validation.');
            setCsvFile(null);
            return;
          }

          setCsvData(validRows.map((row) => ({
            street: row.street.trim(),
            city: row.city.trim(),
            state: row.state.trim(),
            zip_code: row.zip_code.trim(),
            new_to_school: parseNewToSchool(row.new_to_school),
          })));
        },
        error: (err) => {
          setCsvError(`Failed to parse CSV: ${err.message}`);
          setCsvFile(null);
        },
      });
    },
    [enqueueSnackbar]
  );

  // ── Shared: fetch polygons + run point-in-polygon for a given set of markers ──

  const applyPreciseDriveTimes = useCallback(async (schoolUuid, markers) => {
    // Re-opening a school (or a successful upload) already has minutes on disk.
    // Do not call ESRI again just to retry the handful of unroutable leftovers —
    // that 502s and shows an orange warning even though the median is ready.
    if (hasStoredPreciseDriveTimes(markers)) {
      setProcessingSteps((prev) => ({ ...prev, routing: 'skipped' }));
      return markers;
    }
    setProcessingSteps((prev) => ({ ...prev, routing: 'loading' }));
    try {
      const response = await apiClient.post(`/v2/school/${schoolUuid}/catchment-drive-times`);
      const next = attachDriveMinutes(markers, response.entries);
      setProcessingSteps((prev) => ({ ...prev, routing: 'done' }));
      return next;
    } catch (err) {
      setProcessingSteps((prev) => ({ ...prev, routing: 'error' }));
      enqueueSnackbar(`Drive-time routing unavailable: ${formatRoutingError(err)}`, { variant: 'warning' });
      return markers;
    }
  }, [enqueueSnackbar]);

  const applyNeighborhoods = useCallback(async (schoolUuid, markers) => {
    const missing = (markers || []).some((m) => !m.neighborhood);
    if (!missing) return markers;
    try {
      const response = await apiClient.post(`/v2/school/${schoolUuid}/catchment-neighborhoods`);
      return attachNeighborhoods(markers, response.entries);
    } catch {
      return markers;
    }
  }, []);

  const loadCatchmentPlaces = useCallback(async (schoolUuid) => {
    if (!schoolUuid) return;
    try {
      const resp = await apiClient.get(`/v2/school/${schoolUuid}/catchment-places`);
      const places = (resp?.places || [])
        .filter((p) => p.latitude != null && p.longitude != null)
        .map(entryToPlace);
      setCustomPlaces(places);
      setShowCustomPlaces(true);
      setPlaceTypeFilter(null);
    } catch {
      setCustomPlaces([]);
    }
  }, []);

  const runAnalysisWithMarkers = useCallback(async (markers, school) => {
    trackUsage('catchment_analysis', { schoolUuid: school?.uuid });
    setProcessing(true);
    setProcessingSteps({
      polygons: 'loading',
      geocoding: 'skipped',
      analysis: 'pending',
      routing: 'pending',
    });

    try {
      const esriData = await apiClient.get(`/v2/school/${school.uuid}/esri-market-share`);
      const schoolLoc = { lat: esriData.latitude, lng: esriData.longitude };
      const polygons = {};
      DRIVE_TIMES.forEach((dt) => {
        const timeData = esriData.drive_times?.[dt];
        if (timeData?.drive_time_polygon) {
          const feature = parseDriveTimePolygon(timeData.drive_time_polygon);
          if (feature) polygons[dt] = feature;
        }
      });
      if (Object.keys(polygons).length === 0) {
        throw new Error('No drive-time polygon data found for this school.');
      }
      setProcessingSteps((prev) => ({ ...prev, polygons: 'done', analysis: 'loading' }));

      const { counts, outside, markerZones: zones } = countStudentsInPolygons(markers, polygons);
      setProcessingSteps((prev) => ({ ...prev, analysis: 'done' }));

      const withTimes = await applyPreciseDriveTimes(school.uuid, markers);
      const withHoods = await applyNeighborhoods(school.uuid, withTimes);
      setStudentMarkers(withHoods);

      setDriveTimeGeoJSON(polygons);
      setSchoolLocation(schoolLoc);
      setEsriMarketData(esriData);
      setCatchmentCounts({ counts, outside });
      setMarkerZones(zones);
      setSelectedZone(null);
      setSelectedQuadrants(null);
      await loadCatchmentPlaces(school.uuid);
      setActiveStep(2);
    } catch (err) {
      setProcessingSteps((prev) => ({ ...prev, polygons: 'error' }));
      enqueueSnackbar(`Drive-time polygons unavailable: ${err.message}`, { variant: 'error' });
    } finally {
      setProcessing(false);
    }
  }, [applyPreciseDriveTimes, applyNeighborhoods, loadCatchmentPlaces, enqueueSnackbar, trackUsage]);

  // ── Main processing ───────────────────────────────────────────────────────

  const processData = useCallback(async () => {
    if (!selectedSchool || csvData.length === 0) return;

    trackUsage('catchment_analysis', { schoolUuid: selectedSchool.uuid });
    setProcessing(true);
    setProcessingSteps({
      polygons: 'loading',
      geocoding: 'loading',
      analysis: 'pending',
      routing: 'pending',
    });

    try {
      let geocodedMarkers = [];
      let failed = 0;

      try {
        const saveResponse = await apiClient.post(
          `/v2/school/${selectedSchool.uuid}/catchment-entries`,
          {
            entries: csvData.map((row) => ({
              street: row.street?.trim() || '',
              city: row.city?.trim() || '',
              state: row.state?.trim() || '',
              zip_code: row.zip_code?.trim() || '',
              new_to_school: row.new_to_school,
            })),
          }
        );

        geocodedMarkers = (saveResponse.entries || [])
          .filter((e) => e.latitude != null && e.longitude != null)
          .map(entryToMarker);
        failed = (saveResponse.count || 0) - geocodedMarkers.length;

        setGeocodeCacheStats({
          hits: saveResponse.geocoded - (saveResponse.geocoded ?? 0),
          misses: saveResponse.geocoded ?? geocodedMarkers.length,
        });
        setGeocodeSource('persistent');
        setProcessingSteps((prev) => ({ ...prev, geocoding: 'done' }));

        geocodedMarkers = await applyPreciseDriveTimes(selectedSchool.uuid, geocodedMarkers);
        geocodedMarkers = await applyNeighborhoods(selectedSchool.uuid, geocodedMarkers);
      } catch (err) {
        setProcessingSteps((prev) => ({ ...prev, geocoding: 'error' }));
        enqueueSnackbar(`Geocoding / save failed: ${err.message}`, { variant: 'error' });
        setProcessing(false);
        return;
      }

      if (geocodedMarkers.length === 0) {
        setProcessingSteps((prev) => ({ ...prev, geocoding: 'error' }));
        enqueueSnackbar('All addresses failed to geocode. Check the address format.', {
          variant: 'error',
        });
        setProcessing(false);
        return;
      }

      setStudentMarkers(geocodedMarkers);
      setFailedGeocode(failed);

      setProcessingSteps((prev) => ({ ...prev, polygons: 'loading' }));
      try {
        const esriData = await apiClient.get(
          `/v2/school/${selectedSchool.uuid}/esri-market-share`
        );
        const schoolLoc = { lat: esriData.latitude, lng: esriData.longitude };
        const polygons = {};
        DRIVE_TIMES.forEach((dt) => {
          const timeData = esriData.drive_times?.[dt];
          if (timeData?.drive_time_polygon) {
            const feature = parseDriveTimePolygon(timeData.drive_time_polygon);
            if (feature) polygons[dt] = feature;
          }
        });
        if (Object.keys(polygons).length === 0) {
          throw new Error('No drive-time polygon data found for this school.');
        }
        setProcessingSteps((prev) => ({ ...prev, polygons: 'done', analysis: 'loading' }));

        const { counts, outside, markerZones: zones } = countStudentsInPolygons(geocodedMarkers, polygons);
        setProcessingSteps((prev) => ({ ...prev, analysis: 'done' }));

        setStudentMarkers(geocodedMarkers);

        setDriveTimeGeoJSON(polygons);
        setSchoolLocation(schoolLoc);
        setEsriMarketData(esriData);
        setCatchmentCounts({ counts, outside });
        setMarkerZones(zones);
        setSelectedZone(null);
        setSelectedQuadrants(null);
        await loadCatchmentPlaces(selectedSchool.uuid);
        setActiveStep(2);
      } catch (err) {
        setProcessingSteps((prev) => ({ ...prev, polygons: 'error' }));
        enqueueSnackbar(`Drive-time polygons unavailable: ${err.message}`, { variant: 'error' });
      }
    } finally {
      setProcessing(false);
    }
  }, [selectedSchool, csvData, applyPreciseDriveTimes, applyNeighborhoods, loadCatchmentPlaces, enqueueSnackbar, trackUsage]);

  // ── Change school ────────────────────────────────────────────────────────

  const handleChangeSchool = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('school')) {
      params.delete('school');
      const search = params.toString();
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${search ? `?${search}` : ''}`
      );
    }
    setActiveStep(0);
    setSelectedSchool(null);
    setDriveTimeGeoJSON({});
    setSchoolLocation(null);
    setEsriMarketData(null);
    setCatchmentCounts(null);
    setProcessingSteps({ ...INITIAL_PROCESSING_STEPS });
    setVisibleDriveTimes([5, 10, 15]);
    setSelectedZone(null);
    setSelectedQuadrants(null);
    setShowQuadrantLines(false);
    setMarkerZones([]);
    setShowNearbySchools(false);
    setNearbySchools([]);
    setNearbySchoolsLoading(false);
    setSelectedNearbySchoolUuid(null);
    setSelectedNeighborhood(null);
    setSelectedDistrict(null);
    setNearbyGovernance(['Charter', 'District']);
    setNearbyGrades([]);
    setSameTeamOnly(false);
    setPortfolioScope(PORTFOLIO_SCOPES.DRIVE_TIME);
    setShowTeamDriveTimes(false);
    setTeamDriveTimeOverlays({});
    setTeamDriveTimesLoading(false);
    setNearbyEnrollmentByUuid({});
    setShowAmenities(false);
    setAmenityPlaces([]);
    setNewToSchoolFilter('all');
    setCustomPlaces([]);
    setShowCustomPlaces(true);
    setPlacesUploading(false);
    setPlaceTypeFilter(null);
    setPlacesClearConfirmOpen(false);
    setPlacesUploadOpen(false);
    setTourReplay(false);
    setTourStep(0);
    setBasemap('streets');
    amenityBboxKeyRef.current = '';
    setAddressQuery('');
    setSearchedLocation(null);
    setControlsOpen(false);
    setResultsTableTab('nearby');
    setClearConfirmOpen(false);
    setClearingData(false);
  };

  const confirmClearAndReupload = useCallback(async () => {
    if (!selectedSchool) return;
    setClearingData(true);
    try {
      await apiClient.delete(`/v2/school/${selectedSchool.uuid}/catchment-entries`);
      enqueueSnackbar('Student addresses cleared. Custom places were kept. Upload a new CSV.', { variant: 'info' });
    } catch (err) {
      enqueueSnackbar(`Could not clear data: ${err.message}`, { variant: 'error' });
      setClearingData(false);
      return;
    }
    setCsvFile(null);
    setCsvData([]);
    setCsvError(null);
    setStudentMarkers([]);
    setFailedGeocode(0);
    setDriveTimeGeoJSON({});
    setSchoolLocation(null);
    setEsriMarketData(null);
    setCatchmentCounts(null);
    setGeocodeSource(null);
    setGeocodeCacheStats(null);
    setProcessingSteps({ ...INITIAL_PROCESSING_STEPS });
    setSelectedZone(null);
    setSelectedQuadrants(null);
    setShowQuadrantLines(false);
    setMarkerZones([]);
    setAddressQuery('');
    setSearchedLocation(null);
    setShowNearbySchools(false);
    setShowAmenities(false);
    setAmenityPlaces([]);
    setNewToSchoolFilter('all');
    amenityBboxKeyRef.current = '';
    setClearConfirmOpen(false);
    setClearingData(false);
    setActiveStep(1);
  }, [selectedSchool, enqueueSnackbar]);

  const clearAddressSearch = () => {
    setAddressQuery('');
    setSearchedLocation(null);
    setClearConfirmOpen(false);
    setClearingData(false);
  };

  const handleSearchAddress = async () => {
    const query = addressQuery.trim();
    if (!query) {
      enqueueSnackbar('Enter an address to search.', { variant: 'warning' });
      return;
    }
    setAddressSearching(true);
    try {
      const data = await apiClient.post('/v2/cloud-run/geocode', { addresses: [query] });
      const hit = data?.results?.[0];
      if (!hit?.success || hit.latitude == null || hit.longitude == null) {
        enqueueSnackbar('Could not find that address.', { variant: 'warning' });
        return;
      }
      const lat = hit.latitude;
      const lng = hit.longitude;
      setSearchedLocation({
        lat,
        lng,
        formattedAddress: hit.formatted_address || query,
        driveLabel: driveTimeLabelForPoint(lat, lng, driveTimeGeoJSON),
      });
    } catch (err) {
      enqueueSnackbar(`Address search failed: ${err.message}`, { variant: 'error' });
    } finally {
      setAddressSearching(false);
    }
  };

  const handleExportOnePager = async () => {
    setExporting(true);
    try {
      let logoDataUrl = null;
      try {
        logoDataUrl = await fetchImageAsDataUrl(EDC_LOGO_PATH);
      } catch {
        logoDataUrl = null;
      }

      let mapDataUrl = null;
      try {
        mapDataUrl = await captureLeafletMapImage(mapInstanceRef.current, mapCaptureRef.current);
      } catch {
        mapDataUrl = null;
      }
      if (!mapDataUrl) {
        enqueueSnackbar('Map image could not be captured; exporting headlines and metrics only.', {
          variant: 'warning',
        });
      }

      const total = studentMarkers.length || 1;
      const metrics = [
        { label: RING_LABELS[5], count: ringCounts[5] || 0, pct: Math.round(((ringCounts[5] || 0) / total) * 100) },
        { label: RING_LABELS[10], count: ringCounts[10] || 0, pct: Math.round(((ringCounts[10] || 0) / total) * 100) },
        { label: RING_LABELS[15], count: ringCounts[15] || 0, pct: Math.round(((ringCounts[15] || 0) / total) * 100) },
        {
          label: 'Beyond 15 min',
          count: catchmentCounts?.outside || 0,
          pct: Math.round(((catchmentCounts?.outside || 0) / total) * 100),
        },
      ];

      const html = buildCatchmentOnePagerHtml({
        schoolName: selectedSchool?.name,
        timestamp: new Date().toLocaleString(),
        geocodedCount: totalGeocoded,
        totalCount: totalStudents,
        insights: insights
          ? {
              dominantHeadline: `${insights.dominantPct}% of students live ${insights.dominantLabel}`,
              dominantDetail: `${insights.dominantCount} students — the largest concentration of any single ring.`,
              dominantColor: insights.dominantColor,
              medianHeadline: `Median drive time: ${insights.medianLabel}`,
              medianDetail: insights.usePreciseMedian
                ? 'Typical ESRI drive time; half of students are this close or closer.'
                : insights.medianZone === 'outside'
                  ? 'Half of students live beyond a 15-minute drive.'
                  : 'Half of students live this close or closer.',
              medianColor: insights.medianColor,
              adviceHeadline: insights.focusAdvice?.headline,
              adviceDetail: insights.focusAdvice?.detail,
              adviceColor: insights.focusAdviceColor,
              geoHeadline: insights.isSpread
                ? 'Students are spread evenly in all directions'
                : `Students concentrate to the ${insights.geoLabel}`,
              geoDetail: insights.isSpread
                ? 'No single direction dominates — students come from all quadrants.'
                : `${insights.topQPct}% of addresses fall in the ${insights.geoLabel} quadrant${insights.geoLabel.includes('and') ? 's' : ''}.`,
              retentionHeadline:
                insights.dominantZone !== 'outside' && catchmentCounts?.outside > 0
                  ? `${Math.round((catchmentCounts.outside / totalGeocoded) * 100)}% of students commute more than 15 minutes — a potential retention risk`
                  : undefined,
              retentionDetail:
                insights.dominantZone !== 'outside' && catchmentCounts?.outside > 0
                  ? `${catchmentCounts.outside} students live beyond all drive-time rings.`
                  : undefined,
            }
          : null,
        metrics,
        logoDataUrl,
        mapDataUrl,
      });

      downloadCatchmentOnePagerHtml(
        html,
        catchmentOnePagerFilename(selectedSchool?.name, 'html'),
      );
    } catch (err) {
      enqueueSnackbar(`Export failed: ${err.message}`, { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  // ── Map bounds ───────────────────────────────────────────────────────────

  const mapBounds = useMemo(() => {
    const referencePoly = driveTimeGeoJSON[15] || driveTimeGeoJSON[10] || driveTimeGeoJSON[5];
    if (referencePoly) {
      try {
        return L.geoJSON(referencePoly).getBounds();
      } catch {
        /* fall through */
      }
    }
    if (studentMarkers.length > 0) {
      return L.latLngBounds(studentMarkers.map((m) => [m.lat, m.lng]));
    }
    if (schoolLocation) {
      return L.latLngBounds([[schoolLocation.lat, schoolLocation.lng]]);
    }
    return null;
  }, [driveTimeGeoJSON, studentMarkers, schoolLocation]);

  // Campus plus every expanded portfolio pin, so the widened view keeps the target in frame.
  const portfolioScopePoints = useMemo(() => {
    if (expandedPortfolioSchools.length === 0) return [];
    const points = expandedPortfolioSchools.map((s) => [s.latitude, s.longitude]);
    if (schoolLocation) points.push([schoolLocation.lat, schoolLocation.lng]);
    return points;
  }, [expandedPortfolioSchools, schoolLocation]);

  // ── Derived stats ────────────────────────────────────────────────────────

  const totalGeocoded = studentMarkers.length;
  const totalStudents = totalGeocoded + failedGeocode;

  // When a quadrant filter is active, use filtered counts; otherwise use full counts
  const activeCounts = filteredStats ?? catchmentCounts;
  const activeTotal = filteredStats?.total ?? totalGeocoded;
  const activeRingCounts = filteredStats?.ringCounts ?? ringCounts;
  const hasActiveFilter = !!(selectedQuadrants && selectedQuadrants.size > 0);

  // Shared-address stats: group by the address string to find true household duplicates.
  // (Grouping by lat/lng would falsely flag addresses that geocoded to the same point due to
  //  precision fallback, which is a geocoder issue rather than a true duplicate.)
  const duplicateStats = useMemo(() => {
    if (studentMarkers.length === 0) return null;
    const groups = {};
    studentMarkers.forEach((m) => {
      // Use the original CSV address fields (rawAddress) rather than the geocoder's
      // formatted_address, which can normalize different real addresses to the same string.
      const key = m.rawAddress || (m.address || '').toLowerCase().trim();
      if (!key) return;
      groups[key] = (groups[key] || 0) + 1;
    });
    const sharedAddresses = Object.values(groups).filter((c) => c > 1).length;
    const studentsAtShared = Object.values(groups)
      .filter((c) => c > 1)
      .reduce((s, c) => s + c, 0);
    return sharedAddresses > 0 ? { sharedAddresses, studentsAtShared } : null;
  }, [studentMarkers]);

  // Jittered marker positions: spread overlapping pins in a small circle when NOT clustering.
  // Zone assignments still use original coordinates — jitter is purely visual.
  const jitteredMarkers = useMemo(() => {
    if (clusterMarkers || studentMarkers.length === 0) return studentMarkers;
    const groups = {};
    studentMarkers.forEach((m, i) => {
      const key = `${m.lat.toFixed(6)},${m.lng.toFixed(6)}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(i);
    });
    const dlat = new Float64Array(studentMarkers.length);
    const dlng = new Float64Array(studentMarkers.length);
    const R = 0.00014; // ~15 m radius — visible but not misleading
    Object.values(groups).forEach((indices) => {
      if (indices.length <= 1) return;
      indices.forEach((idx, pos) => {
        const angle = (2 * Math.PI * pos) / indices.length;
        dlat[idx] = R * Math.cos(angle);
        dlng[idx] = R * Math.sin(angle);
      });
    });
    return studentMarkers.map((m, i) => ({ ...m, lat: m.lat + dlat[i], lng: m.lng + dlng[i] }));
  }, [studentMarkers, clusterMarkers]);

  const focusMapOnPoints = useCallback((markers, extraBounds) => {
    mapCaptureRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const map = mapInstanceRef.current;
    if (!map) return;
    const pts = (markers || [])
      .filter((m) => m.lat != null && m.lng != null)
      .map((m) => [m.lat, m.lng]);
    if (extraBounds) {
      pts.push([extraBounds.south, extraBounds.west], [extraBounds.north, extraBounds.east]);
    }
    if (pts.length === 0) return;
    if (pts.length === 1) {
      map.flyTo(pts[0], Math.max(map.getZoom(), 14), { duration: 0.45 });
      return;
    }
    map.fitBounds(pts, { padding: [48, 48], maxZoom: 15, animate: true });
  }, []);

  const handleNeighborhoodRowClick = useCallback((row) => {
    const next = selectedNeighborhood === row.label ? null : row.label;
    setSelectedNeighborhood(next);
    setSelectedDistrict(null);
    if (!next) return;
    const matches = studentMarkers.filter(
      (m) => neighborhoodDisplayLabel(m.neighborhood, m.city, m.zipCode || m.zip_code) === next
    );
    focusMapOnPoints(matches);
  }, [selectedNeighborhood, studentMarkers, focusMapOnPoints]);

  const handleDistrictRowClick = useCallback((row) => {
    const next = selectedDistrict === row.label ? null : row.label;
    setSelectedDistrict(next);
    setSelectedNeighborhood(null);
    if (!next) return;
    if (next !== OUTSIDE_DISTRICT_LABEL) setShowDistrictBoundaries(true);
    const matches = studentMarkers.filter((m) => {
      const label = districtLabelForPoint(m.lat, m.lng, districtBoundaries) || OUTSIDE_DISTRICT_LABEL;
      return label === next;
    });
    let extra = null;
    if (next !== OUTSIDE_DISTRICT_LABEL) {
      (districtBoundaries?.features || []).forEach((feature) => {
        if (districtDisplayName(feature) !== next) return;
        const bounds = geojsonBounds(feature);
        if (!bounds) return;
        extra = extra
          ? {
              south: Math.min(extra.south, bounds.south),
              west: Math.min(extra.west, bounds.west),
              north: Math.max(extra.north, bounds.north),
              east: Math.max(extra.east, bounds.east),
            }
          : bounds;
      });
    }
    focusMapOnPoints(matches, extra);
  }, [selectedDistrict, studentMarkers, districtBoundaries, focusMapOnPoints]);

  // ── CSV template download ─────────────────────────────────────────────────

  const downloadTemplate = () => {
    const csv = Papa.unparse({
      fields: ALLOWED_COLUMNS,
      data: [
        ['123 Main St', 'Springfield', 'IL', '62701', 'yes'],
        ['456 Oak Ave', 'Springfield', 'IL', '62702', ''],
      ],
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_addresses_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPlacesTemplate = () => {
    const csv = Papa.unparse({
      fields: PLACE_REQUIRED_COLUMNS,
      data: [
        ['Stop 12 — Colfax & Josephine', 'bus_stop', '1000 E Colfax Ave', 'Denver', 'CO', '80218'],
        ['Park Hill Clinic', 'health_center', '3031 N High St', 'Denver', 'CO', '80205'],
      ],
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'catchment_places_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePlacesFileUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !selectedSchool) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      enqueueSnackbar('Please upload a .csv file.', { variant: 'error' });
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => (h || '').trim().toLowerCase().replace(/\s+/g, '_'),
      complete: async (results) => {
        const headers = (results.meta.fields || []).map((h) => (h || '').trim().toLowerCase());
        const extraColumns = headers.filter((h) => h && !PLACE_REQUIRED_COLUMNS.includes(h));
        if (extraColumns.length > 0) {
          enqueueSnackbar(
            `Places CSV contains extra columns: "${extraColumns.join('", "')}". ` +
              `Permitted: ${PLACE_REQUIRED_COLUMNS.join(', ')}.`,
            { variant: 'error' }
          );
          return;
        }
        const missingColumns = PLACE_REQUIRED_COLUMNS.filter((c) => !headers.includes(c));
        if (missingColumns.length > 0) {
          enqueueSnackbar(
            `Places CSV is missing: ${missingColumns.join(', ')}.`,
            { variant: 'error' }
          );
          return;
        }
        if (results.data.length > MAX_PLACE_ROWS) {
          enqueueSnackbar(
            `Places CSV exceeds the ${MAX_PLACE_ROWS}-row limit (${results.data.length} rows found).`,
            { variant: 'error' }
          );
          return;
        }
        const validRows = results.data.filter((row) =>
          PLACE_REQUIRED_COLUMNS.every((col) => row[col]?.trim())
        );
        const skipped = results.data.length - validRows.length;
        if (skipped > 0) {
          enqueueSnackbar(
            `${skipped} place row${skipped > 1 ? 's' : ''} with empty fields skipped.`,
            { variant: 'warning' }
          );
        }
        if (validRows.length === 0) {
          enqueueSnackbar('No valid places found in the CSV.', { variant: 'error' });
          return;
        }

        setPlacesUploading(true);
        try {
          const resp = await apiClient.post(`/v2/school/${selectedSchool.uuid}/catchment-places`, {
            places: validRows.map((row) => ({
              name: row.name.trim(),
              type: row.type.trim(),
              street: row.street.trim(),
              city: row.city.trim(),
              state: row.state.trim(),
              zip_code: row.zip_code.trim(),
            })),
          });
          const places = (resp?.places || [])
            .filter((p) => p.latitude != null && p.longitude != null)
            .map(entryToPlace);
          setCustomPlaces(places);
          setShowCustomPlaces(true);
          setPlaceTypeFilter(null);
          setPlacesUploadOpen(false);
          const failed = resp?.failed || 0;
          enqueueSnackbar(
            failed > 0
              ? `Saved ${places.length} places (${failed} could not be geocoded).`
              : `Saved ${places.length} place${places.length === 1 ? '' : 's'}.`,
            { variant: failed > 0 ? 'warning' : 'success' }
          );
        } catch (err) {
          enqueueSnackbar(`Could not save places: ${err.message}`, { variant: 'error' });
        } finally {
          setPlacesUploading(false);
        }
      },
    });
  }, [selectedSchool, enqueueSnackbar]);

  const dismissCatchmentTour = useCallback(() => {
    writeCatchmentTourDismissed();
    setTourDismissed(true);
    setTourReplay(false);
    setTourStep(0);
  }, []);

  const replayCatchmentTour = useCallback(() => {
    setTourReplay(true);
    setTourStep(0);
  }, []);

  const confirmClearPlaces = useCallback(async () => {
    if (!selectedSchool) return;
    try {
      await apiClient.delete(`/v2/school/${selectedSchool.uuid}/catchment-places`);
      setCustomPlaces([]);
      setPlaceTypeFilter(null);
      setPlacesClearConfirmOpen(false);
      enqueueSnackbar('Custom places cleared.', { variant: 'info' });
    } catch (err) {
      enqueueSnackbar(`Could not clear places: ${err.message}`, { variant: 'error' });
    }
  }, [selectedSchool, enqueueSnackbar]);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Container maxWidth="xl" sx={{ py: 3 }} data-tool="student-catchment-explorer">
      {/* ── Header ── */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Student Catchment Explorer
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          Upload student addresses to visualize how many live within each drive-time zone.
        </Typography>
      </Box>

      {/* ── Stepper ── */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label, idx) => (
          <Step key={label} completed={activeStep > idx}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* ═══════════════════════════════════════════════════════════════════
          PHASE 1 — School Selection
      ═══════════════════════════════════════════════════════════════════ */}
      {activeStep === 0 && (
        <CatchmentSchoolPicker
          selectedSchool={selectedSchool}
          onSchoolChange={handleSchoolChange}
          checkingExistingData={checkingExistingData}
          continueLabel={studentMarkers.length > 0 ? 'Re-run with New School' : 'Continue'}
          onContinue={async () => {
              if (!selectedSchool) return;
              setCheckingExistingData(true);
              try {
                const existing = await apiClient.get(
                  `/v2/school/${selectedSchool.uuid}/catchment-entries`
                );
                if (existing?.entries?.length > 0) {
                  const markers = existing.entries
                    .filter((e) => e.latitude != null && e.longitude != null)
                    .map(entryToMarker);
                  setStudentMarkers(markers);
                  setFailedGeocode(existing.entries.length - markers.length);
                  setGeocodeSource('persistent');
                  setGeocodeCacheStats(null);
                  setCsvData(existing.entries.map((e) => ({
                    street: e.street, city: e.city, state: e.state,
                    zip_code: e.zip_code,
                    new_to_school: e.new_to_school === true ? true : e.new_to_school === false ? false : null,
                  })));
                  await runAnalysisWithMarkers(markers, selectedSchool);
                } else {
                  setActiveStep(1);
                }
              } catch (err) {
                enqueueSnackbar(
                  `Could not load saved addresses: ${err.message}. The file is still stored — if this is a new column error, run the catchment migration.`,
                  { variant: 'error' }
                );
                setActiveStep(1);
              } finally {
                setCheckingExistingData(false);
              }
            }}
        />
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          PHASE 2 — CSV Upload
      ═══════════════════════════════════════════════════════════════════ */}
      {activeStep === 1 && (
        <Paper sx={{ p: 4, maxWidth: 680 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6">Upload Student Addresses</Typography>
            <Chip label={selectedSchool?.name} size="small" color="primary" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload a CSV file containing only student address fields. No names, IDs, or other
            personally identifiable information is permitted.
          </Typography>

          {/* Privacy schema card */}
          <Paper
            variant="outlined"
            sx={{ p: 2, mb: 3, bgcolor: 'info.50', borderColor: 'info.200' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <InfoIcon color="info" fontSize="small" />
              <Typography variant="body2" fontWeight={600}>
                Required CSV Schema
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Required: <strong>street, city, state, zip_code</strong>. Optional:{' '}
              <strong>new_to_school</strong> (1/0, true/false, yes/no). No other columns allowed.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {REQUIRED_COLUMNS.map((col) => (
                <Chip key={col} label={col} size="small" color="primary" variant="outlined" />
              ))}
              <Chip label="new_to_school (optional)" size="small" variant="outlined" />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Max {MAX_STUDENT_ROWS} rows. Addresses are saved for this school and can be cleared later.
            </Typography>
          </Paper>

          {/* Session reuse banner */}
          {geocodeSource === 'session' && studentMarkers.length > 0 && (
            <Alert
              severity="info"
              icon={<BoltIcon fontSize="inherit" />}
              sx={{ mb: 2 }}
            >
              <strong>{studentMarkers.length} addresses from current session.</strong> Geocoding will
              be skipped — only the school's drive-time polygons will be re-fetched.
            </Alert>
          )}

          {/* Upload area */}
          <Box
            sx={{
              border: '2px dashed',
              borderColor: csvFile ? 'success.main' : 'divider',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: csvFile ? 'success.50' : 'grey.50',
              mb: 2,
            }}
          >
            <CloudUploadIcon
              sx={{ fontSize: 40, color: csvFile ? 'success.main' : 'action.disabled', mb: 1 }}
            />
            {csvFile ? (
              <>
                <Typography variant="body1" fontWeight={600} color="success.dark">
                  {csvFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {csvData.length} valid row{csvData.length !== 1 ? 's' : ''} ready
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Select a CSV file to upload
              </Typography>
            )}
            <Button
              variant="outlined"
              component="label"
              size="small"
              sx={{ mt: 1.5 }}
            >
              {csvFile ? 'Choose Different File' : 'Choose File'}
              <input type="file" accept=".csv" hidden onChange={handleFileUpload} />
            </Button>
          </Box>

          {csvError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {csvError}
            </Alert>
          )}

          {/* Preview table */}
          {csvData.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Preview (first 5 rows of {csvData.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {(csvData.some((r) => r.new_to_school === true || r.new_to_school === false)
                        ? ALLOWED_COLUMNS
                        : REQUIRED_COLUMNS
                      ).map((col) => (
                        <TableCell key={col} sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                          {col.replace('_', ' ')}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvData.slice(0, 5).map((row, i) => (
                      <TableRow key={i}>
                        {(csvData.some((r) => r.new_to_school === true || r.new_to_school === false)
                          ? ALLOWED_COLUMNS
                          : REQUIRED_COLUMNS
                        ).map((col) => (
                          <TableCell key={col}>
                            {col === 'new_to_school'
                              ? row.new_to_school === true ? 'Yes' : row.new_to_school === false ? 'No' : '—'
                              : row[col]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Processing status */}
          {processing && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Processing…
              </Typography>
              {[
                { key: 'polygons', label: 'Loading drive-time polygons' },
                {
                  key: 'geocoding',
                  label:
                    processingSteps.geocoding === 'skipped'
                      ? `Geocoding skipped — using ${
                          studentMarkers.length > 0 ? 'session' : 'cached'
                        } data`
                      : `Geocoding ${csvData.length} addresses`,
                },
                { key: 'analysis', label: 'Running catchment analysis' },
                {
                  key: 'routing',
                  label:
                    processingSteps.routing === 'skipped'
                      ? 'Drive times already stored'
                      : 'Routing drive times…',
                },
              ].map(({ key, label }) => (
                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <StepStatusIcon status={processingSteps[key]} />
                  <Typography
                    variant="body2"
                    color={
                      processingSteps[key] === 'done'
                        ? 'success.main'
                        : processingSteps[key] === 'error'
                        ? 'error.main'
                        : 'text.secondary'
                    }
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
              <LinearProgress sx={{ mt: 1 }} />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              disabled={!csvFile || csvData.length === 0 || processing}
              onClick={processData}
              startIcon={processing ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {processing ? 'Processing…' : 'Run Analysis'}
            </Button>
            <Button variant="text" onClick={() => setActiveStep(0)} disabled={processing}>
              Back
            </Button>
            <Tooltip title="Download a CSV template with the correct column headers">
              <Button
                variant="text"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={downloadTemplate}
                sx={{ ml: 'auto' }}
              >
                Download Template
              </Button>
            </Tooltip>
          </Box>
        </Paper>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          PHASE 3 — Results
      ═══════════════════════════════════════════════════════════════════ */}
      {activeStep === 2 && catchmentCounts && (
        <>
          {/* Results header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h6">{selectedSchool?.name}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {totalGeocoded} of {totalStudents} address{totalStudents !== 1 ? 'es' : ''} geocoded
                </Typography>
                {failedGeocode > 0 && (
                  <Chip label={`${failedGeocode} failed`} size="small" color="warning" />
                )}
                {geocodeSource === 'persistent' && (
                  <Chip label="Loaded from saved data" size="small" color="success" icon={<BoltIcon />} />
                )}
                {geocodeSource === 'session' && (
                  <Chip label="Reused from session" size="small" color="info" icon={<BoltIcon />} />
                )}
                {geocodeCacheStats && geocodeCacheStats.hits > 0 && (
                  <Chip
                    label={`${geocodeCacheStats.hits} from DB cache`}
                    size="small"
                    color="success"
                    icon={<BoltIcon />}
                  />
                )}
                {geocodeCacheStats && geocodeCacheStats.misses > 0 && (
                  <Chip
                    label={`${geocodeCacheStats.misses} newly geocoded`}
                    size="small"
                    color="default"
                    variant="outlined"
                  />
                )}
                {duplicateStats && (
                  <Tooltip title={`${duplicateStats.studentsAtShared} students geocode to ${duplicateStats.sharedAddresses} shared address${duplicateStats.sharedAddresses !== 1 ? 'es' : ''} (e.g. siblings). Each student is counted individually. In individual-pin mode, overlapping pins are spread apart.`}>
                    <Chip
                      label={`${duplicateStats.studentsAtShared} of ${totalGeocoded} at shared address${duplicateStats.sharedAddresses !== 1 ? 'es' : ''}`}
                      size="small"
                      color="default"
                      variant="outlined"
                      sx={{ cursor: 'help' }}
                    />
                  </Tooltip>
                )}
                {selectedZone !== null && (
                  <Chip
                    label={`Zone: ${
                      selectedZone === 'outside'
                        ? 'Beyond 15-min'
                        : cumulativeMode
                        ? `Within ${selectedZone}-min`
                        : RING_LABELS[selectedZone]
                    }`}
                    size="small"
                    color="primary"
                    onDelete={() => {
                      setSelectedZone(null);
                      setVisibleDriveTimes([5, 10, 15]);
                    }}
                  />
                )}
                {selectedQuadrants && selectedQuadrants.size > 0 && (
                  <Chip
                    label={`Quadrants: ${[...selectedQuadrants].sort().join(', ')}`}
                    size="small"
                    color="secondary"
                    onDelete={() => setSelectedQuadrants(null)}
                  />
                )}
                {selectedNeighborhood && (
                  <Chip
                    label={`Neighborhood: ${selectedNeighborhood}`}
                    size="small"
                    color="primary"
                    onDelete={() => setSelectedNeighborhood(null)}
                  />
                )}
                {selectedDistrict && (
                  <Chip
                    label={`District: ${selectedDistrict}`}
                    size="small"
                    color="primary"
                    onDelete={() => setSelectedDistrict(null)}
                  />
                )}
              </Box>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {tourDismissed && !showingTour && (
                <Button
                  variant="outlined"
                  startIcon={<HelpOutlineIcon />}
                  onClick={replayCatchmentTour}
                  aria-label="Replay catchment tour"
                  sx={{ textTransform: 'none' }}
                >
                  Tour
                </Button>
              )}
              <Tooltip title="Keep the geocoded addresses, pick a different school">
                <Button
                  variant="outlined"
                  startIcon={<SchoolIcon />}
                  onClick={handleChangeSchool}
                >
                  Change School
                </Button>
              </Tooltip>
              <Tooltip title="Download a branded one-pager of this map, headlines, and key metrics">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                    disabled={exporting || !catchmentCounts}
                    onClick={handleExportOnePager}
                  >
                    Export
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Permanently delete saved student addresses for this school. Custom places are kept.">
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<DeleteForeverIcon />}
                  onClick={() => setClearConfirmOpen(true)}
                >
                  Clear & Re-upload
                </Button>
              </Tooltip>
            </Stack>
          </Box>

          {/* ── Recommendation ── */}
          {insights && (() => {
            const focusIsHighlighted = selectedZone === insights.focusDriveTime;
            if (insights.focusAdvice && recommendationDismissed) {
              return (
                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    startIcon={<TipsAndUpdatesIcon />}
                    onClick={() => {
                      setRecommendationDismissed(false);
                      setRecommendationCollapsed(false);
                    }}
                    sx={{ color: insights.focusAdviceColor }}
                  >
                    Show recommendation
                  </Button>
                </Box>
              );
            }
            if (!insights.focusAdvice) return null;
            return (
              <Paper
                elevation={0}
                sx={{
                  mb: 1.25,
                  p: recommendationCollapsed ? 1 : 1.75,
                  display: 'flex',
                  gap: 1.5,
                  alignItems: recommendationCollapsed ? 'center' : { xs: 'flex-start', sm: 'center' },
                  flexWrap: recommendationCollapsed ? 'nowrap' : 'wrap',
                  bgcolor: alpha(insights.focusAdviceColor, 0.1),
                  border: '1px solid',
                  borderColor: alpha(insights.focusAdviceColor, 0.45),
                  borderLeft: `5px solid ${insights.focusAdviceColor}`,
                  borderRadius: 1.5,
                }}
              >
                <TipsAndUpdatesIcon sx={{ color: insights.focusAdviceColor, mt: recommendationCollapsed ? 0 : { xs: 0.25, sm: 0 }, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="overline"
                    sx={{ display: 'block', lineHeight: 1.2, letterSpacing: 0.12, color: insights.focusAdviceColor, fontWeight: 700 }}
                  >
                    Recommendation
                  </Typography>
                  <Typography
                    variant={recommendationCollapsed ? 'body2' : 'h6'}
                    sx={{
                      fontWeight: 700,
                      lineHeight: 1.25,
                      mb: recommendationCollapsed ? 0 : 0.35,
                      overflow: recommendationCollapsed ? 'hidden' : 'visible',
                      textOverflow: recommendationCollapsed ? 'ellipsis' : 'clip',
                      whiteSpace: recommendationCollapsed ? 'nowrap' : 'normal',
                    }}
                  >
                    {insights.focusAdvice.headline}
                  </Typography>
                  {!recommendationCollapsed && (
                    <Typography variant="body2" color="text.secondary">
                      {insights.focusAdvice.detail}
                    </Typography>
                  )}
                </Box>
                {!recommendationCollapsed && (
                  <Button
                    size="small"
                    variant={focusIsHighlighted ? 'contained' : 'outlined'}
                    onClick={() => {
                      const focus = insights.focusDriveTime;
                      if (focus == null) return;
                      if (focusIsHighlighted) {
                        setSelectedZone(null);
                        setVisibleDriveTimes([5, 10, 15]);
                        return;
                      }
                      setSelectedZone(focus);
                      setVisibleDriveTimes(
                        typeof focus === 'number' ? [focus] : [5, 10, 15]
                      );
                    }}
                    sx={{
                      flexShrink: 0,
                      borderColor: insights.focusAdviceColor,
                      color: focusIsHighlighted ? '#fff' : insights.focusAdviceColor,
                      bgcolor: focusIsHighlighted ? insights.focusAdviceColor : 'transparent',
                      '&:hover': {
                        borderColor: insights.focusAdviceColor,
                        bgcolor: focusIsHighlighted
                          ? insights.focusAdviceColor
                          : alpha(insights.focusAdviceColor, 0.12),
                      },
                    }}
                  >
                    {focusIsHighlighted ? 'Showing this ring' : 'Highlight this ring'}
                  </Button>
                )}
                <Box sx={{ display: 'flex', flexShrink: 0, ml: 'auto' }}>
                  <Tooltip title={recommendationCollapsed ? 'Expand recommendation' : 'Minimize recommendation'}>
                    <IconButton
                      size="small"
                      aria-label={recommendationCollapsed ? 'Expand recommendation' : 'Minimize recommendation'}
                      onClick={() => setRecommendationCollapsed((prev) => !prev)}
                    >
                      {recommendationCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Close recommendation">
                    <IconButton
                      size="small"
                      aria-label="Close recommendation"
                      onClick={() => setRecommendationDismissed(true)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            );
          })()}

          <Grid container spacing={1} sx={{ mb: 1.5 }} alignItems="stretch" data-catchment-tour="drive-metrics">
            {insights && (
              <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    borderLeft: `4px solid ${insights.medianColor}`,
                    display: 'flex',
                    gap: 1.25,
                    alignItems: 'center',
                    width: '100%',
                    borderRadius: 1.5,
                  }}
                >
                  <DirectionsCarIcon sx={{ color: insights.medianColor, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                      Median drive time: {insights.medianLabel}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
                      {insights.usePreciseMedian
                        ? 'Typical ESRI drive time; half of students are this close or closer.'
                        : insights.medianZone === 'outside'
                          ? 'Half of students live beyond a 15-minute drive.'
                          : 'Half of students live this close or closer.'}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )}
            {DRIVE_TIMES.map((dt) => {
              const count = cumulativeMode ? activeCounts.counts[dt] : activeRingCounts[dt];
              const pct = activeTotal > 0 ? Math.round((count / activeTotal) * 100) : 0;
              const isSelected = selectedZone === dt;
              const label = cumulativeMode ? DRIVE_TIME_LABELS[dt] : RING_LABELS[dt];
              const esriContext = (() => {
                if (!esriMarketData) return null;
                const ages5to17 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];
                const fromGrades = targetSchoolGrades.flatMap((g) => GRADE_TO_AGE_IDX[g] || [])
                  .filter((i) => i >= 1);
                const ageIdxSet = new Set(fromGrades.length > 0 ? fromGrades : ages5to17);
                const sumAges = (arr) => arr
                  ? [...ageIdxSet].reduce((s, i) => s + (arr[i] || 0), 0)
                  : 0;
                const thisArr = esriMarketData?.drive_times?.[dt]?.ages?.['4_17']?.current;
                if (!thisArr) return null;
                let esriPop;
                if (cumulativeMode) {
                  esriPop = Math.round(sumAges(thisArr));
                } else {
                  const prevDt = dt === 5 ? null : dt === 10 ? 5 : 10;
                  const prevArr = prevDt
                    ? esriMarketData?.drive_times?.[prevDt]?.ages?.['4_17']?.current
                    : null;
                  esriPop = Math.round(sumAges(thisArr) - sumAges(prevArr));
                }
                if (!esriPop || esriPop <= 0) return null;
                const penetration = esriPop > 0 ? ((count / esriPop) * 100).toFixed(1) : null;
                const ageLabel = getAgeRangeLabel(ageIdxSet);
                return { esriPop, penetration, ageLabel };
              })();
              return (
                <Grid item xs={6} sm={6} md={3} key={dt} sx={{ display: 'flex' }}>
                  <Tooltip title="Click to highlight these students on the map" placement="top">
                    <Card
                      variant="outlined"
                      onClick={() => setSelectedZone(isSelected ? null : dt)}
                      sx={{
                        cursor: 'pointer',
                        width: '100%',
                        height: '100%',
                        borderLeft: `4px solid ${driveTimeColors[dt]}`,
                        bgcolor: isSelected ? alpha(driveTimeColors[dt], 0.08) : 'background.paper',
                        outline: isSelected ? `2px solid ${driveTimeColors[dt]}` : 'none',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: isSelected ? alpha(driveTimeColors[dt], 0.12) : 'action.hover',
                        },
                      }}
                    >
                      <CardContent sx={{ py: 0.75, px: 1.25, '&:last-child': { pb: 0.75 } }}>
                        <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.6rem', lineHeight: 1.2 }}>
                          {label}
                          {hasActiveFilter && (
                            <Typography component="span" sx={{ fontSize: '0.58rem', ml: 0.5, color: 'secondary.main' }}>
                              (filtered)
                            </Typography>
                          )}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
                          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.15 }}>{pct}%</Typography>
                          <Typography variant="caption" color="text.secondary">({count} students)</Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          sx={{
                            mt: 0.5, height: 3, borderRadius: 2, bgcolor: 'grey.100',
                            '& .MuiLinearProgress-bar': { bgcolor: driveTimeColors[dt] },
                          }}
                        />
                        {esriContext && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4, fontSize: '0.6rem', lineHeight: 1.3 }}>
                            ~{esriContext.esriPop.toLocaleString()} children {esriContext.ageLabel} (ESRI)
                            {esriContext.penetration && (
                              <> · <strong>{esriContext.penetration}%</strong> enrolled</>
                            )}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Tooltip>
                </Grid>
              );
            })}
          </Grid>

          {/* ── Map controls ── */}
          <Paper
            sx={{
              mb: 2,
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: alpha('#2563eb', 0.035),
              border: '1.5px solid',
              borderColor: alpha('#2563eb', 0.45),
              boxShadow: '0 6px 18px rgba(15, 23, 42, 0.08)',
              animation: `${mapControlsReveal} 0.95s ease-out`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          >
            <Box
              onClick={() => setControlsOpen((o) => !o)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.1,
                cursor: 'pointer',
                bgcolor: alpha('#2563eb', 0.06),
                '&:hover': { bgcolor: alpha('#2563eb', 0.1) },
              }}
            >
              <TuneIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                Map controls
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                {!controlsOpen && controlsSummary.slice(0, 4).map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.7rem' }}
                  />
                ))}
                {!controlsOpen && controlsSummary.length > 4 && (
                  <Chip
                    label={`+${controlsSummary.length - 4}`}
                    size="small"
                    sx={{ height: 22, fontSize: '0.7rem' }}
                  />
                )}
                {showNearbySchools && (
                  <Chip
                    label={
                      nearbySchoolsLoading
                        ? 'Loading nearby…'
                        : teamDriveTimesLoading
                          ? `${filteredNearbySchools.length} shown · loading catchments…`
                          : `${filteredNearbySchools.length} shown`
                    }
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
              >
                <Tooltip
                  title={
                    cumulativeMode
                      ? 'Cumulative: a student in the 5-min ring is also counted in the 10-min and 15-min totals.'
                      : 'By Ring: each zone shows only students exclusively within that ring (no overlap).'
                  }
                >
                  <ToggleButtonGroup
                    value={cumulativeMode ? 'cumulative' : 'ring'}
                    exclusive
                    onChange={(_, val) => { if (val !== null) setCumulativeMode(val === 'cumulative'); }}
                    size="small"
                  >
                    <ToggleButton value="cumulative" sx={{ py: 0.15, px: 0.9, fontSize: '0.68rem', lineHeight: 1.4 }}>
                      Cumulative
                    </ToggleButton>
                    <ToggleButton value="ring" sx={{ py: 0.15, px: 0.9, fontSize: '0.68rem', lineHeight: 1.4 }}>
                      By Ring
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Tooltip>
              </Box>
              <IconButton
                size="small"
                aria-label={controlsOpen ? 'Collapse map controls' : 'Expand map controls'}
              >
                {controlsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={controlsOpen}>
              <Divider />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 2, py: 1.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    minWidth: 92, fontSize: '0.65rem', fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                    color: 'text.secondary', flexShrink: 0,
                  }}
                >
                  Layers
                </Typography>
                <FormGroup row sx={{ gap: 0.5 }}>
                  {DRIVE_TIMES.map((dt) => (
                    <FormControlLabel
                      key={dt}
                      control={
                        <Checkbox
                          size="small"
                          checked={visibleDriveTimes.includes(dt)}
                          onChange={(e) =>
                            setVisibleDriveTimes((prev) =>
                              e.target.checked
                                ? [...prev, dt].sort((a, b) => a - b)
                                : prev.filter((v) => v !== dt)
                            )
                          }
                          sx={{
                            color: driveTimeColors[dt],
                            '&.Mui-checked': { color: driveTimeColors[dt] },
                            p: 0.25,
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 14, height: 3, bgcolor: driveTimeColors[dt], borderRadius: 1 }} />
                          <Typography variant="caption">{dt}-min</Typography>
                        </Box>
                      }
                      sx={{ mr: 0.5, ml: 0 }}
                    />
                  ))}
                </FormGroup>
                <FormControlLabel
                  control={<Switch size="small" checked={clusterMarkers} onChange={(e) => setClusterMarkers(e.target.checked)} />}
                  label={<Typography variant="caption">Cluster pins</Typography>}
                  sx={{ mr: 0, ml: 0 }}
                />
                <FormControlLabel
                  control={<Switch size="small" checked={showDistrictBoundaries} onChange={(e) => setShowDistrictBoundaries(e.target.checked)} />}
                  label={<Typography variant="caption">District Boundaries</Typography>}
                  sx={{ mr: 0, ml: 0 }}
                />
                <FormControlLabel
                  control={<Switch size="small" checked={showQuadrantLines} onChange={(e) => setShowQuadrantLines(e.target.checked)} />}
                  label={<Typography variant="caption">Quadrant lines</Typography>}
                  sx={{ mr: 0, ml: 0 }}
                />
                <FormControlLabel
                  control={<Switch size="small" checked={showAmenities} onChange={(e) => setShowAmenities(e.target.checked)} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="caption">Libraries & centers</Typography>
                      {amenitiesLoading && <CircularProgress size={12} />}
                    </Box>
                  }
                  sx={{ mr: 0, ml: 0 }}
                />
                <FormControlLabel
                  data-catchment-tour="nearby-schools"
                  control={
                    <Switch
                      size="small"
                      checked={showNearbySchools}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setShowNearbySchools(on);
                        if (!on) {
                          setShowTeamDriveTimes(false);
                          setSelectedNearbySchoolUuid(null);
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="caption">Nearby schools</Typography>
                      {nearbySchoolsLoading && <CircularProgress size={12} />}
                    </Box>
                  }
                  sx={{ mr: 0, ml: 0 }}
                />
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 2, py: 1.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    minWidth: 92, fontSize: '0.65rem', fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                    color: 'text.secondary', flexShrink: 0,
                  }}
                >
                  Places
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showCustomPlaces && customPlaces.length > 0}
                      disabled={customPlaces.length === 0}
                      onChange={(e) => setShowCustomPlaces(e.target.checked)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="caption">
                        {customPlaces.length > 0
                          ? `${customPlaces.length} custom place${customPlaces.length === 1 ? '' : 's'}`
                          : 'Custom places'}
                      </Typography>
                      {placesUploading && <CircularProgress size={12} />}
                    </Box>
                  }
                  sx={{ mr: 0, ml: 0 }}
                />
                {placeTypeOptions.length > 1 && customPlaces.length > 0 && (
                  <ToggleButtonGroup
                    size="small"
                    value={placeTypeFilter ? [...placeTypeFilter] : placeTypeOptions.map((o) => o.label)}
                    onChange={(_e, val) => {
                      if (!val || val.length === 0 || val.length === placeTypeOptions.length) {
                        setPlaceTypeFilter(null);
                        return;
                      }
                      setPlaceTypeFilter(new Set(val));
                    }}
                  >
                    {placeTypeOptions.map((opt) => (
                      <ToggleButton
                        key={opt.label}
                        value={opt.label}
                        sx={{ py: 0.15, px: 0.9, fontSize: '0.65rem' }}
                      >
                        {opt.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}
                <Button
                  data-catchment-tour="custom-places"
                  size="small"
                  variant="outlined"
                  startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
                  disabled={placesUploading}
                  onClick={() => setPlacesUploadOpen(true)}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                >
                  {customPlaces.length > 0 ? 'Replace custom data' : 'Upload custom data'}
                </Button>
                {customPlaces.length > 0 && (
                  <Button
                    size="small"
                    color="warning"
                    variant="text"
                    startIcon={<DeleteForeverIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setPlacesClearConfirmOpen(true)}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                  >
                    Clear places
                  </Button>
                )}
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 2, py: 1.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    minWidth: 92, fontSize: '0.65rem', fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                    color: 'text.secondary', flexShrink: 0,
                  }}
                >
                  Students
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  value={selectedQuadrants ? [...selectedQuadrants] : QUADRANT_LABELS}
                  onChange={(_e, val) => {
                    if (!val || val.length === 0) {
                      setSelectedQuadrants(null);
                      return;
                    }
                    if (val.length === QUADRANT_LABELS.length) {
                      setSelectedQuadrants(null);
                      return;
                    }
                    setSelectedQuadrants(new Set(val));
                  }}
                >
                  {QUADRANT_LABELS.map((q) => (
                    <ToggleButton key={q} value={q} sx={{ py: 0.15, px: 0.9, fontSize: '0.65rem' }}>
                      {q}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                {hasNewToSchoolTags && (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={newToSchoolFilter}
                    onChange={(_e, val) => { if (val != null) setNewToSchoolFilter(val); }}
                  >
                    <ToggleButton value="all" sx={{ py: 0.15, px: 0.75, fontSize: '0.65rem' }}>All</ToggleButton>
                    <ToggleButton value="new" sx={{ py: 0.15, px: 0.75, fontSize: '0.65rem' }}>New</ToggleButton>
                    <ToggleButton value="existing" sx={{ py: 0.15, px: 0.75, fontSize: '0.65rem' }}>Existing</ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Box>
              {showNearbySchools && (
                <>
                  <Divider />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 2, py: 1.25 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: 92, fontSize: '0.65rem', fontWeight: 600,
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                        color: 'text.secondary', flexShrink: 0,
                      }}
                    >
                      Nearby
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      value={nearbyGovernance}
                      onChange={(_e, val) => setNearbyGovernance(val)}
                    >
                      {NEARBY_GOVERNANCE_OPTIONS.map((opt) => (
                        <ToggleButton key={opt} value={opt} sx={{ py: 0.15, px: 0.9, fontSize: '0.65rem' }}>
                          {opt}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    <Autocomplete
                      multiple
                      size="small"
                      options={GRADE_ORDER}
                      value={nearbyGrades}
                      onChange={(_e, val) => setNearbyGrades(val)}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option}
                            label={option}
                            size="small"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={nearbyGrades.length === 0 ? 'All grades' : ''}
                          label="Grades offered"
                        />
                      )}
                      sx={{ minWidth: 220, maxWidth: 320 }}
                    />
                    <Tooltip
                      title={
                        hasPortfolioSchools
                          ? 'Show only nearby schools in the same portfolio that offer overlapping grades'
                          : 'This school is not assigned to a portfolio'
                      }
                    >
                      <span>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={sameTeamOnly}
                              disabled={!hasPortfolioSchools}
                              onChange={(e) => setSameTeamOnly(e.target.checked)}
                            />
                          }
                          label={<Typography variant="caption">Within Portfolio</Typography>}
                          sx={{ mr: 0, ml: 0 }}
                        />
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        !hasPortfolioSchools
                          ? 'This school is not assigned to a portfolio'
                          : targetCountyKnown
                            ? `Which portfolio campuses appear on the map: only those inside the drive-time rings, every campus in ${targetCountyLabel}, or your whole portfolio. Campuses still need to offer the grades selected above.`
                            : 'Which portfolio campuses appear on the map: only those inside the drive-time rings, or your whole portfolio. Same county needs a county for this campus, which is not available right now.'
                      }
                    >
                      <span>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', flexShrink: 0 }}
                          >
                            Portfolio reach
                          </Typography>
                          <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={portfolioScope}
                            disabled={!hasPortfolioSchools}
                            onChange={(_e, val) => { if (val != null) setPortfolioScope(val); }}
                          >
                            <ToggleButton
                              value={PORTFOLIO_SCOPES.DRIVE_TIME}
                              sx={{ py: 0.15, px: 0.9, fontSize: '0.65rem' }}
                            >
                              Drive time
                            </ToggleButton>
                            <ToggleButton
                              value={PORTFOLIO_SCOPES.COUNTY}
                              disabled={!targetCountyKnown}
                              sx={{ py: 0.15, px: 0.9, fontSize: '0.65rem' }}
                            >
                              Same county
                            </ToggleButton>
                            <ToggleButton
                              value={PORTFOLIO_SCOPES.ALL}
                              sx={{ py: 0.15, px: 0.9, fontSize: '0.65rem' }}
                            >
                              All portfolio
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        hasPortfolioSchools
                          ? 'Show 5/10/15-minute rings for other portfolio schools that offer overlapping grades, so you can see catchment overlap.'
                          : 'This school is not assigned to a portfolio'
                      }
                    >
                      <span>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={showTeamDriveTimes}
                              disabled={!hasPortfolioSchools}
                              onChange={(e) => setShowTeamDriveTimes(e.target.checked)}
                            />
                          }
                          label={<Typography variant="caption">Portfolio Drive Times</Typography>}
                          sx={{ mr: 0, ml: 0 }}
                        />
                      </span>
                    </Tooltip>
                  </Box>
                </>
              )}
            </Collapse>
          </Paper>

              <Paper sx={{ overflow: 'hidden', borderRadius: 2, position: 'relative' }} data-catchment-tour="map">
                <Box
                  sx={{
                    position: 'absolute',
                    zIndex: 1000,
                    top: 12,
                    left: 54,
                    width: { xs: 'calc(100% - 168px)', sm: 360 },
                    pointerEvents: 'none',
                  }}
                >
                  <Paper elevation={3} sx={{ pointerEvents: 'auto', p: 0.5 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search an address"
                      value={addressQuery}
                      onChange={(e) => setAddressQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSearchAddress();
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            {addressSearching ? (
                              <CircularProgress size={16} />
                            ) : searchedLocation || addressQuery ? (
                              <IconButton size="small" onClick={clearAddressSearch} aria-label="Clear address search">
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            ) : null}
                            <Button size="small" onClick={handleSearchAddress} disabled={addressSearching}>
                              Search
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {searchedLocation && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, pb: 0.5 }}>
                        {searchedLocation.driveLabel} from campus
                      </Typography>
                    )}
                  </Paper>
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    zIndex: 1000,
                    top: 12,
                    right: 12,
                    pointerEvents: 'none',
                  }}
                >
                  <Paper elevation={3} sx={{ pointerEvents: 'auto', p: 0.25 }} data-catchment-tour="basemap">
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      orientation="vertical"
                      value={basemap}
                      onChange={(_e, val) => { if (val) setBasemap(val); }}
                    >
                      <ToggleButton value="streets" sx={{ py: 0.35, px: 0.9, fontSize: '0.65rem' }}>Streets</ToggleButton>
                      <ToggleButton value="satellite" sx={{ py: 0.35, px: 0.9, fontSize: '0.65rem' }}>Satellite</ToggleButton>
                      <ToggleButton value="detailed" sx={{ py: 0.35, px: 0.9, fontSize: '0.65rem' }}>Detailed streets</ToggleButton>
                    </ToggleButtonGroup>
                  </Paper>
                </Box>
                <Box
                  data-catchment-tour="legend"
                  sx={{
                    position: 'absolute',
                    zIndex: 1000,
                    bottom: 24,
                    left: 12,
                    pointerEvents: 'none',
                  }}
                >
                  <CatchmentMapLegend
                    searchedLocation={searchedLocation}
                    showNearbySchools={showNearbySchools}
                    showTeamDriveTimes={showTeamDriveTimes}
                    hasExpandedPortfolio={expandedPortfolioSchools.length > 0}
                    hasNewToSchoolTags={hasNewToSchoolTags}
                    showAmenities={showAmenities}
                    customPlaceLegendItems={customPlaceLegendItems}
                  />
                </Box>
                <Box ref={mapCaptureRef}>

                <MapContainer
                  center={
                    schoolLocation
                      ? [schoolLocation.lat, schoolLocation.lng]
                      : [39.5, -98.35]
                  }
                  zoom={11}
                  style={{ height: 680 }}
                  scrollWheelZoom
                >
                  <MapReady mapRef={mapInstanceRef} />
                  <TileLayer
                    key={basemap}
                    url={BASEMAPS[basemap].url}
                    attribution={BASEMAPS[basemap].attribution}
                  />

                  {mapBounds && (
                    <MapFitBounds
                      bounds={mapBounds}
                      disabled={
                        Boolean(searchedLocation)
                        || Boolean(selectedNearbySchoolUuid)
                        || Boolean(selectedNeighborhood)
                        || Boolean(selectedDistrict)
                      }
                    />
                  )}
                  {searchedLocation && (
                    <MapFitSearched schoolLocation={schoolLocation} searchedLocation={searchedLocation} />
                  )}
                  <MapFitPortfolioScope
                    scope={portfolioScope}
                    points={portfolioScopePoints}
                    disabled={Boolean(searchedLocation) || Boolean(selectedNearbySchoolUuid)}
                  />

                  {showDistrictBoundaries && districtBoundaries?.features?.length > 0 && (
                    <DistrictBoundariesLayer
                      data={districtBoundaries}
                      schoolUuid={selectedSchool?.uuid}
                      selectedName={
                        selectedDistrict && selectedDistrict !== OUTSIDE_DISTRICT_LABEL
                          ? selectedDistrict
                          : null
                      }
                    />
                  )}

                  {/* Drive-time polygon layers (largest first so smaller render on top) */}
                  {[15, 10, 5].map((dt) => {
                    if (!visibleDriveTimes.includes(dt) || !driveTimeGeoJSON[dt]) return null;
                    return (
                      <GeoJSON
                        key={`${dt}-${selectedZone ?? 'all'}`}
                        data={driveTimeGeoJSON[dt]}
                        style={() => driveTimePolygonStyle(dt, selectedZone)}
                      />
                    );
                  })}

                  {showTeamDriveTimes && Object.entries(teamDriveTimeOverlays).flatMap(([uuid, overlay]) =>
                    [15, 10, 5].map((dt) => {
                      if (!visibleDriveTimes.includes(dt) || !overlay.polygons?.[dt]) return null;
                      return (
                        <GeoJSON
                          key={`team-${uuid}-${dt}`}
                          data={overlay.polygons[dt]}
                          style={() => ({
                            color: TEAM_DRIVE_TIME_COLOR,
                            weight: 2.5,
                            opacity: 0.95,
                            fillColor: TEAM_DRIVE_TIME_COLOR,
                            fillOpacity: 0.12,
                            dashArray: '8 5',
                          })}
                          onEachFeature={(_feature, layer) => {
                            layer.bindPopup(
                              `<strong>${overlay.name || 'Portfolio school'}</strong><br/>${dt}-min drive`
                            );
                          }}
                        />
                      );
                    })
                  )}

                  {/* Quadrant crosshair lines (±2° extends well beyond all drive-time rings) */}
                  {showQuadrantLines && schoolLocation && (
                    <>
                      <Polyline
                        positions={[
                          [schoolLocation.lat, schoolLocation.lng - 2],
                          [schoolLocation.lat, schoolLocation.lng + 2],
                        ]}
                        pathOptions={{ color: '#475569', weight: 1.5, opacity: 0.55, dashArray: '8 5' }}
                      />
                      <Polyline
                        positions={[
                          [schoolLocation.lat - 2, schoolLocation.lng],
                          [schoolLocation.lat + 2, schoolLocation.lng],
                        ]}
                        pathOptions={{ color: '#475569', weight: 1.5, opacity: 0.55, dashArray: '8 5' }}
                      />
                    </>
                  )}

                  {/* Student address markers with zone and quadrant filtering */}
                  {(() => {
                    const pins = jitteredMarkers.map((marker, i) => {
                      const innermostZone = markerZones[i] ?? null;
                      if (!isMarkerHighlighted(innermostZone, selectedZone, cumulativeMode)) return null;
                      const origMarker = studentMarkers[i];
                      // Quadrant filter (use original coordinates for quadrant calculation)
                      if (selectedQuadrants && selectedQuadrants.size > 0 && schoolLocation) {
                        const isNorth = origMarker.lat >= schoolLocation.lat;
                        const isEast = origMarker.lng >= schoolLocation.lng;
                        const q = isNorth ? (isEast ? 'NE' : 'NW') : (isEast ? 'SE' : 'SW');
                        if (!selectedQuadrants.has(q)) return null;
                      }
                      if (newToSchoolFilter === 'new' && origMarker.newToSchool !== true) return null;
                      if (newToSchoolFilter === 'existing' && origMarker.newToSchool !== false) return null;
                      if (selectedNeighborhood) {
                        const hoodLabel = neighborhoodDisplayLabel(
                          origMarker.neighborhood,
                          origMarker.city,
                          origMarker.zipCode || origMarker.zip_code
                        );
                        if (hoodLabel !== selectedNeighborhood) return null;
                      }
                      if (selectedDistrict) {
                        const districtLabel = districtLabelForPoint(
                          origMarker.lat,
                          origMarker.lng,
                          districtBoundaries
                        ) || OUTSIDE_DISTRICT_LABEL;
                        if (districtLabel !== selectedDistrict) return null;
                      }
                      const pinColor = hasNewToSchoolTags
                        ? studentPinColor(origMarker.newToSchool)
                        : EXISTING_STUDENT_COLOR;
                      return (
                        <CircleMarker
                          key={i}
                          center={[marker.lat, marker.lng]}
                          radius={5}
                          pathOptions={{
                            color: pinColor,
                            fillColor: pinColor,
                            fillOpacity: 0.8,
                            weight: 1,
                          }}
                        >
                          <Popup minWidth={195} maxWidth={250}>
                            <div style={{ minWidth: 185, fontFamily: 'inherit', lineHeight: 1.3 }}>
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5, marginBottom: 7 }}>
                                <LocationOnIcon style={{ fontSize: 13, color: '#64748b', marginTop: 1, flexShrink: 0 }} />
                                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.35 }}>
                                  {(origMarker.address || '').replace(/,\s*USA$/i, '')}
                                </span>
                              </div>
                              <hr style={{ margin: '0 0 7px', border: 'none', borderTop: '1px solid #e2e8f0' }} />
                              <div style={{ fontSize: '0.68rem', color: '#475569', marginBottom: 6 }}>
                                Neighborhood: {neighborhoodDisplayLabel(origMarker.neighborhood, origMarker.city, origMarker.zipCode)}
                              </div>
                              {innermostZone && (() => {
                                const minutes = origMarker.driveTimeMinutes;
                                const usePrecise = typeof minutes === 'number'
                                  || processingSteps.routing === 'done';
                                const driveLabel = usePrecise
                                  ? (typeof minutes === 'number'
                                    ? `${Math.round(minutes)} min drive`
                                    : 'Beyond 60 min')
                                  : innermostZone === 'outside'
                                    ? 'Beyond 15-min drive'
                                    : `Within ${innermostZone}-min drive`;
                                const driveColor = usePrecise
                                  ? colorForMinutes(minutes)
                                  : innermostZone === 'outside'
                                    ? '#9ca3af'
                                    : driveTimeColors[innermostZone];
                                return (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                  <DirectionsCarIcon style={{ fontSize: 13, flexShrink: 0, color: driveColor }} />
                                  <span style={{ fontSize: '0.68rem', fontWeight: 600, color: driveColor }}>
                                    {driveLabel}
                                  </span>
                                </div>
                                );
                              })()}
                            </div>
                          </Popup>
                        </CircleMarker>
                      );
                    });
                    return clusterMarkers ? (
                      <MarkerClusterGroup
                        chunkedLoading
                        showCoverageOnHover={false}
                        maxClusterRadius={40}
                      >
                        {pins}
                      </MarkerClusterGroup>
                    ) : pins;
                  })()}

                  {showNearbySchools && filteredNearbySchools.map((school) => {
                    const isCharter = school.charter === 'Yes' || school.is_charter === true;
                    const isOnTeam = Boolean(teamSchoolUuids?.has(school.uuid))
                      && schoolSharesGradesWithTarget(school, targetSchoolGrades);
                    const color = isCharter ? NEARBY_CHARTER_COLOR : NEARBY_DISTRICT_COLOR;
                    return (
                      <NearbySchoolMapMarker
                        key={school.uuid}
                        school={school}
                        selected={selectedNearbySchoolUuid === school.uuid}
                        popupNonce={nearbyPopupNonce}
                        color={color}
                        isCharter={isCharter}
                        isOnTeam={isOnTeam}
                        metrics={nearbySchoolMetrics(nearbyEnrollmentByUuid[school.uuid], nearbyMetricGrades)}
                      />
                    );
                  })}

                  {expandedPortfolioSchools.map((school) => (
                    <NearbySchoolMapMarker
                      key={`portfolio-${school.uuid}`}
                      school={school}
                      selected={selectedNearbySchoolUuid === school.uuid}
                      popupNonce={nearbyPopupNonce}
                      color={TEAM_DRIVE_TIME_COLOR}
                      isCharter={school.charter === 'Yes' || school.is_charter === true}
                      isOnTeam
                      metrics={nearbySchoolMetrics(nearbyEnrollmentByUuid[school.uuid], nearbyMetricGrades)}
                    />
                  ))}

                  {showAmenities && amenityPlaces.map((place) => (
                    <Marker
                      key={place.id}
                      position={[place.lat, place.lng]}
                      icon={poiMapIcon(place.kind)}
                      zIndexOffset={300}
                    >
                      <Popup>
                        <div style={{ fontWeight: 700, fontSize: '0.78rem' }}>{place.name}</div>
                        <div style={{ fontSize: '0.68rem', color: '#64748b' }}>
                          {place.kind === 'library' ? 'Library' : 'Community center'}
                        </div>
                      </Popup>
                    </Marker>
                  ))}

                  {visibleCustomPlaces.map((place) => (
                    <Marker
                      key={`place-${place.id}`}
                      position={[place.lat, place.lng]}
                      icon={placeMapIcon(place.kind)}
                      zIndexOffset={400}
                    >
                      <Popup>
                        <div style={{ fontWeight: 700, fontSize: '0.78rem' }}>{place.name}</div>
                        <div style={{ fontSize: '0.68rem', color: '#64748b' }}>{place.typeLabel}</div>
                        <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: 2 }}>
                          {driveTimeLabelForPoint(place.lat, place.lng, driveTimeGeoJSON)}
                        </div>
                        {(place.formattedAddress || place.street) && (
                          <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 2 }}>
                            {place.formattedAddress || `${place.street}, ${place.city}`}
                          </div>
                        )}
                      </Popup>
                    </Marker>
                  ))}

                  {schoolLocation && (
                    <Marker
                      position={[schoolLocation.lat, schoolLocation.lng]}
                      icon={SELECTED_SCHOOL_STAR_ICON}
                      zIndexOffset={2000}
                    >
                      <Popup>
                        <strong>{selectedSchool?.name}</strong>
                        <br />
                        School location
                      </Popup>
                    </Marker>
                  )}

                  {searchedLocation && schoolLocation && (
                    <Polyline
                      positions={[
                        [schoolLocation.lat, schoolLocation.lng],
                        [searchedLocation.lat, searchedLocation.lng],
                      ]}
                      pathOptions={{ color: '#2563eb', weight: 2.5, dashArray: '8 6', opacity: 0.9 }}
                    />
                  )}
                  {searchedLocation && (
                    <Marker
                      position={[searchedLocation.lat, searchedLocation.lng]}
                      zIndexOffset={1000}
                      eventHandlers={{ add: (e) => e.target.openPopup() }}
                    >
                      <Popup>
                        <div style={{ minWidth: 160 }}>
                          <div style={{ fontWeight: 700, fontSize: '0.8rem', marginBottom: 4 }}>
                            {searchedLocation.formattedAddress}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#2563eb', fontWeight: 600 }}>
                            {searchedLocation.driveLabel}
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  )}
                </MapContainer>
                </Box>

              </Paper>

          {(() => {
            const nearbyAvailable = showNearbySchools && (nearbySchoolsLoading || nearbySchoolTableRows.length > 0);
            const hoodsAvailable = neighborhoodSummary.length > 0;
            const districtsAvailable = districtSummary.length > 0;
            if (!nearbyAvailable && !hoodsAvailable && !districtsAvailable) return null;
            const tabAvailable = {
              nearby: nearbyAvailable,
              neighborhoods: hoodsAvailable,
              districts: districtsAvailable,
            };
            const activeTab = tabAvailable[resultsTableTab]
              ? resultsTableTab
              : nearbyAvailable
                ? 'nearby'
                : hoodsAvailable
                  ? 'neighborhoods'
                  : 'districts';
            return (
              <Paper sx={{ mt: 2, overflow: 'hidden', borderRadius: 2 }} data-catchment-tour="results-table">
                <Tabs
                  value={activeTab}
                  onChange={(_e, val) => setResultsTableTab(val)}
                  sx={{ px: 1, minHeight: 44, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  {nearbyAvailable && (
                    <Tab
                      value="nearby"
                      label={`Nearby schools (${nearbySchoolsLoading ? '…' : nearbySchoolTableRows.length})`}
                      sx={{ minHeight: 44, textTransform: 'none', fontWeight: 700 }}
                    />
                  )}
                  {hoodsAvailable && (
                    <Tab
                      value="neighborhoods"
                      label={`Neighborhoods (${neighborhoodSummary.length})`}
                      sx={{ minHeight: 44, textTransform: 'none', fontWeight: 700 }}
                    />
                  )}
                  {districtsAvailable && (
                    <Tab
                      value="districts"
                      label={`Districts (${districtSummary.length})`}
                      sx={{ minHeight: 44, textTransform: 'none', fontWeight: 700 }}
                    />
                  )}
                </Tabs>

                {activeTab === 'nearby' && nearbySchoolsLoading && (
                  <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={24} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Loading nearby schools…
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Matching campuses to this catchment. This can take a few seconds.
                      </Typography>
                    </Box>
                  </Box>
                )}

                {activeTab === 'nearby' && !nearbySchoolsLoading && nearbySchoolTableRows.length > 0 && (
                  <>
                    <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        {nearbySchoolTableRows.length} matching the map filters
                        {' · '}portfolio schools listed first
                        {expandedPortfolioSchools.length > 0
                          ? ` · ${expandedPortfolioSchools.length} portfolio school${expandedPortfolioSchools.length === 1 ? '' : 's'} beyond the drive times, listed last`
                          : ''}
                        {portfolioHiddenByGrades > 0
                          ? ` · ${portfolioHiddenByGrades} portfolio campus${portfolioHiddenByGrades === 1 ? '' : 'es'} hidden because ${portfolioHiddenByGrades === 1 ? 'it does' : 'they do'} not offer the selected grades`
                          : ''}
                        {' · '}click a school to see it on the map
                      </Typography>
                    </Box>
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader sx={CATCHMENT_TABLE_SX}>
                  <TableHead>
                    <TableRow>
                      <TableCell>School</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Grades</TableCell>
                      <TableCell align="right">2024-2025 Enrollment</TableCell>
                      <TableCell align="right">5-Year Trend</TableCell>
                      <TableCell>2024-2025 Rating</TableCell>
                      <TableCell>Portfolio</TableCell>
                      <TableCell>Catchment overlap</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nearbySchoolTableRows.map((row) => (
                      <TableRow
                        key={row.uuid}
                        hover
                        selected={selectedNearbySchoolUuid === row.uuid}
                        onClick={() => {
                          setSelectedNearbySchoolUuid(row.uuid);
                          setNearbyPopupNonce((n) => n + 1);
                          mapCaptureRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          const map = mapInstanceRef.current;
                          if (map && row.lat != null && row.lng != null) {
                            map.flyTo(
                              [row.lat, row.lng],
                              Math.max(map.getZoom(), 13),
                              { duration: 0.45 }
                            );
                          }
                        }}
                        sx={{
                          ...CATCHMENT_TABLE_ROW_SX,
                          cursor: 'pointer',
                          bgcolor: row.isTeam && selectedNearbySchoolUuid !== row.uuid
                            ? alpha(TEAM_DRIVE_TIME_COLOR, 0.08)
                            : undefined,
                          '&.Mui-selected': {
                            bgcolor: alpha('#1976d2', 0.12),
                          },
                        }}
                      >
                        <TableCell
                          component="th"
                          scope="row"
                          sx={{ fontWeight: row.isTeam ? 600 : 400 }}
                        >
                          {row.name}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: row.type === 'Charter' ? NEARBY_CHARTER_COLOR : NEARBY_DISTRICT_COLOR,
                          }}
                        >
                          {row.type}
                        </TableCell>
                        <TableCell>{row.grades || '—'}</TableCell>
                        <TableCell align="right">
                          {row.enrollment != null ? row.enrollment.toLocaleString() : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {row.trendPct == null ? (
                            <Box sx={{ color: 'text.secondary' }}>{row.trendLabel}</Box>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 0.5,
                                color: row.trendPct > 0 ? 'success.main' : row.trendPct < 0 ? 'error.main' : 'text.secondary',
                                fontWeight: 600,
                              }}
                            >
                              {row.trendPct > 0
                                ? <TrendingUpIcon fontSize="small" />
                                : row.trendPct < 0
                                  ? <TrendingDownIcon fontSize="small" />
                                  : null}
                              {`${row.trendPct > 0 ? '+' : ''}${Math.round(row.trendPct)}%`}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ fontWeight: 600, ...getAcademicColorStyle(row.academicMetric) }}>
                            {row.academicLabel}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {row.isTeam ? (
                            <Chip
                              label="Portfolio"
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                bgcolor: alpha(TEAM_DRIVE_TIME_COLOR, 0.15),
                                color: TEAM_DRIVE_TIME_COLOR,
                              }}
                            />
                          ) : (
                            <Box sx={{ color: 'text.secondary' }}>No</Box>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.catchmentDt != null ? (
                            <Chip
                              label={`${row.catchmentDt}-min`}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                bgcolor: alpha(driveTimeColors[row.catchmentDt], 0.16),
                                color: driveTimeColors[row.catchmentDt],
                              }}
                            />
                          ) : (
                            <Box sx={{ color: 'text.secondary' }}>
                              {row.isExpanded
                                ? `Outside ${maxVisibleDriveTime}-min drive time`
                                : 'Outside'}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
                  </>
                )}

                {activeTab === 'neighborhoods' && (
                  <>
                    <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        Google neighborhood when available, otherwise city + ZIP
                        {' · '}click a row to highlight those students on the map
                      </Typography>
                    </Box>
                    <CatchmentShareTable
                      rows={neighborhoodSummary}
                      nameHeader="Neighborhood"
                      selectedLabel={selectedNeighborhood}
                      onRowClick={handleNeighborhoodRowClick}
                    />
                  </>
                )}

                {activeTab === 'districts' && (
                  <>
                    <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        From student coordinates and Census school district boundaries
                        {' · '}click a row to highlight it on the map
                      </Typography>
                    </Box>
                    <CatchmentShareTable
                      rows={districtSummary}
                      nameHeader="District"
                      selectedLabel={selectedDistrict}
                      onRowClick={handleDistrictRowClick}
                    />
                  </>
                )}
              </Paper>
            );
          })()}

          {showingTour && (
            <StudentCatchmentTour
              step={tourStep}
              onStepChange={setTourStep}
              onDismiss={dismissCatchmentTour}
              layoutKey={`${controlsOpen}-${tourStep}`}
            />
          )}

        </>
      )}

      <Dialog
        open={clearConfirmOpen}
        onClose={() => {
          if (!clearingData) setClearConfirmOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>This cannot be undone</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Clearing data permanently deletes the saved student addresses
            {selectedSchool?.name ? ` for ${selectedSchool.name}` : ''}.
            Drive times and map results for this school will be lost. Custom places
            (bus stops, health centers, and other landmarks) are kept. There is no way
            to revert — you will need to upload a new student CSV to restore a catchment.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setClearConfirmOpen(false)} disabled={clearingData}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={clearingData}
            startIcon={clearingData ? <CircularProgress size={16} color="inherit" /> : <DeleteForeverIcon />}
            onClick={confirmClearAndReupload}
          >
            {clearingData ? 'Deleting…' : 'Delete permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={placesClearConfirmOpen}
        onClose={() => setPlacesClearConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Clear custom places?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This deletes the saved landmarks
            {selectedSchool?.name ? ` for ${selectedSchool.name}` : ''}
            (bus stops, health centers, and other places). Student addresses are not affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPlacesClearConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            startIcon={<DeleteForeverIcon />}
            onClick={confirmClearPlaces}
          >
            Clear places
          </Button>
        </DialogActions>
      </Dialog>

      <input
        ref={placesFileInputRef}
        type="file"
        accept=".csv"
        hidden
        onChange={handlePlacesFileUpload}
      />
      <Dialog
        open={placesUploadOpen}
        onClose={() => {
          if (!placesUploading) setPlacesUploadOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1.25 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
              flexShrink: 0,
            }}
          >
            <CloudUploadIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            {customPlaces.length > 0 ? 'Replace custom data' : 'Upload custom data'}
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, mt: 0.15 }}>
              Landmarks for this school — not student addresses
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Add bus stops, health centers, preschools, or other places you care about.
            This does not replace student addresses or change enrollment counts.
          </DialogContentText>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}
          >
            Required columns
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
              p: 1.25,
              mb: 0.75,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            {PLACE_REQUIRED_COLUMNS.map((col) => (
              <Chip
                key={col}
                label={col}
                size="small"
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  bgcolor: '#fff',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Maximum {MAX_PLACE_ROWS} rows.
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}
          >
            Known types
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
              p: 1.25,
              mb: 0.75,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            {PLACE_KNOWN_TYPES.map((opt) => {
              const color = placeTypeColor(opt.kind);
              return (
                <Box
                  key={opt.slug}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.5,
                    width: '100%',
                  }}
                >
                  {opt.examples.map((example, idx) => (
                    <React.Fragment key={example}>
                      {idx > 0 && (
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 800, color: 'text.secondary', px: 0.15 }}
                        >
                          =
                        </Typography>
                      )}
                      <Chip
                        size="small"
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {idx === 0 && (
                              <Box
                                component="span"
                                sx={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: '50%',
                                  bgcolor: alpha(color, 0.16),
                                  color,
                                  fontSize: '0.65rem',
                                  fontWeight: 800,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {placeTypeLetter(opt.kind)}
                              </Box>
                            )}
                            <Box component="span" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 700 }}>
                              {example}
                            </Box>
                          </Box>
                        }
                        sx={{
                          height: 28,
                          bgcolor: '#fff',
                          border: '1px solid',
                          borderColor: alpha(color, 0.35),
                        }}
                      />
                    </React.Fragment>
                  ))}
                </Box>
              );
            })}
          </Box>
          <Paper
            variant="outlined"
            sx={{
              p: 1.25,
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
              borderRadius: 1.5,
              bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
              borderColor: (theme) => alpha(theme.palette.info.main, 0.3),
            }}
          >
            <InfoIcon color="info" fontSize="small" sx={{ mt: 0.15, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                Underscores, spaces, and hyphens are interchangeable
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                <strong>bus_stop</strong>, <strong>bus stop</strong>, and <strong>bus-stop</strong> all map to the same type.
                Other values become custom labels.
              </Typography>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            startIcon={<DownloadIcon />}
            onClick={downloadPlacesTemplate}
            sx={{ textTransform: 'none', mr: 'auto' }}
          >
            Download template
          </Button>
          <Button onClick={() => setPlacesUploadOpen(false)} disabled={placesUploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={placesUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
            disabled={placesUploading}
            onClick={() => placesFileInputRef.current?.click()}
            sx={{ textTransform: 'none' }}
          >
            {placesUploading ? 'Uploading…' : 'Choose CSV'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StudentCatchmentTool;
